import { Component, OnInit, effect, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService, EnumValues, ModalService, Note, NotesSidebarComponent, EnumObjectExtended } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import ObjectId from 'bson-objectid';
import _ from 'lodash';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { catchError, combineLatest, debounceTime, lastValueFrom, map, of, startWith } from 'rxjs';
import { fadeInRight, fadeOutRight } from '../../../animations/purchase-animations';
import { BusinessSearchCommand } from '../../../models/business-search';
import { BusinessEntityType } from '../../../models/business-type-entities';
import { ClientPurchaseOrder, ClientPurchaseOrderSearchCommand } from '../../../models/client-purchase-order/client-purchase-order';
import { Address } from '../../../models/finance/address';
import { Business } from '../../../models/finance/business';
import { EntityKey } from '../../../models/inventory/keys/entity-key';
import { Invoice, S3Object } from '../../../models/invoice/invoice';
import { YarnDetail } from '../../../models/item/greige';
import { HierarchyLevel } from '../../../models/item/enum/hierarchy-level';
import {
  Certification,
  ItemCategory,
  ItemMaster,
  ItemSearchCommand,
  SERVICES_ITEM_CLASSIFICATION_CODE,
} from '../../../models/item/item-master/item-master';
import { CustomerOrder } from '../../../models/order/CustomerOrder';
import { Quotation, QuotationSearchCommand } from '../../../models/order/quotation';
import { WorkOrder, WorkOrderFabric, WorkOrderStatus, WorkOrderType } from '../../../models/order/work-order';
import { Currency } from '../../../models/purchase-order/currency';
import { PurchaseOrderStatus, PurchaseOrderType } from '../../../models/purchase-order/enum-purchase';
import { PurchaseOrder, PurchaseOrderInsert, PurchaseOrderItem } from '../../../models/purchase-order/purchase-order';
import { ExternalRequestEntry } from '../../../models/requisitions/interfaces/external-request-entry';
import { ExternalRequisition } from '../../../models/requisitions/interfaces/external-requisition';
import { AdministrativePurchaseOrderRoles, PlantManagerGroup, PurchaseOrderRoles } from '../../../models/roles/roles-group';
import { Shipment } from '../../../models/shipment/shipment';
import { UserLite } from '../../../models/user-master/user-lite';
import { MatchingItemMasterCommand } from '../../../models/yarn/yarn-search';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { ExchangeRateProxyService } from '../../../proxies/finance/exchange-rate.proxy.service';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { CertificationProxyService } from '../../../proxies/item/certification-proxy.service';
import { ItemCategoryProxyService } from '../../../proxies/item/item-category.proxy.service';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { MasterDataProxyService } from '../../../proxies/item/master-data.proxy.service';
import { YarnSearchProxyService } from '../../../proxies/item/yarn.proxy.service';
import { ClientPurchaseOrderProxyService } from '../../../proxies/order/client-purchase-order.proxy.service';
import { CustomerOrderProxyService } from '../../../proxies/order/customer-order.proxy.service';
import { InvoiceProxyService } from '../../../proxies/order/invoice.proxy.service';
import { PurchaseOrderProxyService } from '../../../proxies/order/purchase-order.proxy.service';
import { QuotationProxyService } from '../../../proxies/order/quotation.proxy.service';
import { RequisitionProxyService } from '../../../proxies/order/requisition.proxy.service';
import { WorkOrderProxyService } from '../../../proxies/order/work-order.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { GlobalMessageUtil, getLocalizedValue } from '@card-software/nexus-utilities';
import { MasterDataActionUtil } from '../../../utilities/mater-data-action-util';
import { YarnQuantityUtil } from '../../../utilities/yarn-quantity.util';
import { ShipmentDetailModalComponent } from '../../_modals/shipment-detail-modal/shipment-detail-modal.component';
import {
  ApiResponse,
  AuthService,
  EntityKeyByReference,
  GlobalEntityDefinition,
  MasterData,
  PaginatedApiResponse,
} from '@card-software/nexus-utilities';

const CLIENT_PO_ENTITY_DEFINITION_CODE = 39; // GlobalEntityDefinition.ClientPO from backend
export interface ItemFormGroup {
  id: FormControl<string>;
  itemCode: FormControl<string>;
  longDescription?: FormControl<string | null>;
  uom: FormControl<number | null>;
  quantity: FormControl<number>;
  unitPrice: FormControl<number>;
  discount: FormControl<number | null>;
  isTaxExempt: FormControl<boolean>; // UI-only field for tax exemption
  totalPrice: FormControl<number>;
  owner: FormControl<EntityKey | null>;
}

@Component({
  selector: 'app-purchase-order-details',
  templateUrl: './purchase-order-details.component.html',
  styleUrls: ['./purchase-order-details.component.scss'],
  animations: [fadeInRight, fadeOutRight],
})
export class PurchaseOrderDetailsComponent implements OnInit {
  //#region Core Properties
  purchaseOrder: PurchaseOrder | null = null;
  currentDate = new Date();
  issuedDate: Date = new Date();
  quotedDate: Date = new Date();
  isLoading = 0;
  drawerView = false;
  //#endregion

  //#region Enums and Master Data
  enumValues: EnumValues = {};
  statusEnumObject?: EnumObjectExtended;
  uoms: EnumValues = {};
  workOrderStatus: EnumValues = {};
  currencyEnum: EnumValues = {};
  orderTypeEnums: EnumValues = {};
  workOrderShipmentTypeEnums!: EnumValues;
  certifications: Certification[] = [];
  itemsCategories = signal<ItemCategory[]>([]);
  //#endregion

  //#region Notes
  userLite: UserLite | null = null;
  notes: Note[] = [];
  //#endregion

  //#region Form Controls - Main
  status = new FormControl(0, { nonNullable: true, validators: Validators.required });
  type = new FormControl<number | null>(null, { nonNullable: true, validators: [Validators.required] });
  provider = new FormControl<Business | null>(null, Validators.required);
  providerAddress = new FormControl<Address | null>({ value: null, disabled: true }, Validators.required);
  currency = new FormControl(0, { nonNullable: true });
  exchangeRateToUSD = new FormControl<number | null>(null);
  items = new FormArray<FormGroup<ItemFormGroup>>([]);
  //#endregion

  //#region Form Controls - Bill To / Ship To
  // Bill To and Ship To are always the site's own company (#5969): set by the page, never edited.
  billToProvider = new FormControl<Business | null>({ value: null, disabled: true }, Validators.required);
  billToAddress = new FormControl<Address | null>({ value: null, disabled: true }, Validators.required);
  paymentMethod = new FormControl<string | null>(null, Validators.required);
  paymentTerms = new FormControl<string | null>(null, Validators.required);
  shipToProvider = new FormControl<Business | null>({ value: null, disabled: true }, Validators.required);
  shipToAddress = new FormControl<Address | null>({ value: null, disabled: true }, Validators.required);
  shippingMethod = new FormControl<string | null>(null, Validators.required);
  shippingTerms = new FormControl<number | null>(null, Validators.required);
  //#endregion

  //#region Form Controls - Standard Specific
  destinationLocationCode = new FormControl<string | null>(null, Validators.required);
  destinationLocationOptions: { label: string; value: string }[] = [];
  //#endregion

  //#region Form Controls - Yarn Specific
  manufacturer = new FormControl<Business | null>(null);
  manufacturerSameAsProvider = new FormControl<boolean>(false, { nonNullable: true });
  //#endregion

  //#region Form Group
  formGroup = new FormGroup({
    provider: this.provider,
    status: this.status,
    items: this.items,
    billToProvider: this.billToProvider,
    billToAddress: this.billToAddress,
    paymentMethod: this.paymentMethod,
    paymentTerms: this.paymentTerms,
    shipToProvider: this.shipToProvider,
    shipToAddress: this.shipToAddress,
    shippingMethod: this.shippingMethod,
    shippingTerms: this.shippingTerms,
    currency: this.currency,
    exchangeRateToUSD: this.exchangeRateToUSD,
    providerAddress: this.providerAddress,
  });
  //#endregion

  //#region Data Arrays - Providers and Addresses
  providers: Business[] = [];
  providerAddresses: Address[] = [];
  billToProviders: Business[] = [];
  billToAddresses: Address[] = [];
  shipToProviders: Business[] = [];
  shipToAddresses: Address[] = [];
  invoices: Invoice[] = [];
  shipments: Shipment[] = [];
  //#endregion

  //#region Data Arrays - Items and Search
  filteredItems: ItemMaster[] = [];
  filteredCustomerOrders: CustomerOrder[] = [];
  searchItemInput = new FormControl('');
  itemCategoryFilter = new FormControl<ItemCategory | null>(null);
  //#endregion

  //#region Data Arrays - Yarn Specific
  manufacturers: Business[] = [];
  makersToDisplay: Business[] = [];
  referenceMakerOriginal = '';
  //#endregion

  //#region Associated Entities - Common
  formControlEntity = new FormControl<number | null>({ value: null, disabled: false });
  entities: { label: string; value: number }[] = [];
  readonly workOrderEntityCode = GlobalEntityDefinition.WorkOrder;
  readonly quotationEntityCode = GlobalEntityDefinition.Quotation;
  //#endregion

  //#region Associated Entities - Standard PO
  // Una sola quotation permitida
  selectedQuotation: Quotation | null = null;
  formControlQuotation = new FormControl<Quotation | null>(null);
  suggestedQuotations: Quotation[] = [];

  // Múltiples requisitions
  associatedRequisitions: ExternalRequisition[] = [];
  formControlRequisition = new FormControl<ExternalRequisition | null>(null);
  suggestedRequisitions: ExternalRequisition[] = [];

  // Tracking de la última requisition seleccionada para asignar como owner a nuevos items
  currentRequisitionOwner: EntityKey | null = null;

  // Legacy (mantener para compatibilidad con flujo existente desde query params)
  quotation: Quotation | null = null;
  externalRequisition: ExternalRequisition | null = null;
  //#endregion

  //#region Associated Entities - Yarn PO
  formControlWorkOrder = new FormControl<WorkOrder | null>(null);
  suggestionWorkOrders: WorkOrder[] = [];
  associateWorkOrders: WorkOrder[] = [];
  filteredEntities: ItemMaster[] = [];
  WorkOrderSelectedToSearch = new FormControl<WorkOrder | null>({ value: null, disabled: false });
  yarnUniqueKeyAndItemMaster: Record<string, ItemMaster | null> = {};
  //#endregion

  //#region Associated Entities - Chemical PO
  readonly clientPOEntityCode = CLIENT_PO_ENTITY_DEFINITION_CODE;
  formControlClientPO = new FormControl<ClientPurchaseOrder | null>(null);
  suggestedClientPOs: (ClientPurchaseOrder & { fieldToDisplay?: string })[] = [];
  associatedClientPOs: ClientPurchaseOrder[] = [];
  //#endregion

  //#region Computed Properties
  get isYarnPurchaseOrder(): boolean {
    return this.type.value === PurchaseOrderType.Yarn;
  }

  get isStandardPurchaseOrder(): boolean {
    return this.type.value === PurchaseOrderType.Standard;
  }

  get isAdministrativePurchaseOrder(): boolean {
    return this.type.value === PurchaseOrderType.Administrative;
  }

  /**
   * Standard and Administrative share one flow (#5942): quotation + requisitions as related entities, a
   * destination warehouse, PlantManager approval. Administrative only adds the role gate and Services items.
   */
  get followsStandardFlow(): boolean {
    return this.isStandardPurchaseOrder || this.isAdministrativePurchaseOrder;
  }

  get isChemicalPurchaseOrder(): boolean {
    return this.type.value === PurchaseOrderType.Chemicals;
  }

  get isEditing(): boolean {
    return !_.isNil(this.purchaseOrder);
  }

  get showHnlEquivalent(): boolean {
    return this.purchaseOrder?.currency !== Currency.HNL || this.currency.value !== Currency.HNL;
  }

  get currentCurrency(): string {
    const currencyValue = this.purchaseOrder?.currency ?? this.currency.value;
    return this.currencyEnum[currencyValue] || 'USD';
  }

  get getTotalQuantity(): number {
    return this.items.controls.reduce((acc, item) => acc + item.controls.quantity.value, 0);
  }

  get getTotalPrice(): number {
    return this.items.controls.reduce((acc, item) => acc + item.controls.totalPrice.value, 0);
  }

  get getTotalPriceUSD(): number {
    return this.items.controls.reduce((acc, item) => acc + this.getValueOnUSD(item.controls.totalPrice.value || 0), 0);
  }

  get showShipments(): boolean {
    // Check both purchaseOrder.shipments and local shipments array (for copy flow)
    return _.isEmpty(this.allShipments);
  }

  get allShipments(): Shipment[] {
    // Combine shipments from purchaseOrder and local shipments array (for copy flow)
    // Filter out cancelled shipments (status = 7)
    const poShipments = (this.purchaseOrder?.shipments ?? []).filter((shipment) => shipment.status !== 7);
    const localShipments = this.shipments.filter((shipment) => shipment.status !== 7);
    return [...poShipments, ...localShipments];
  }

  /** Holds the role that alone governs Administrative POs (#5942): create, edit, submit, approve, reject, cancel. */
  get isAdministrativeAdmin(): boolean {
    return AuthService.hasRole(AdministrativePurchaseOrderRoles.AdministrativePurchaseOrderAdmin);
  }

  get canChangeStatus(): boolean {
    // Administrative POs answer only to their own role; PlantManager and Admin/Editor have no say (#5942).
    if (this.isAdministrativePurchaseOrder) {
      const status = this.purchaseOrder?.status;
      return this.isAdministrativeAdmin && (!status || status === PurchaseOrderStatus.Draft || status === PurchaseOrderStatus.Submitted);
    }

    const hasEditRole =
      AuthService.hasRole(PurchaseOrderRoles.PurchaseOrderAdmin) || AuthService.hasRole(PurchaseOrderRoles.PurchaseOrderEditor);
    const isPlantManager = AuthService.hasRole(PlantManagerGroup.PlantManager);
    const isStandardPO = this.followsStandardFlow;
    const currentStatus = this.purchaseOrder?.status;

    // For Standard POs that are Submitted: ONLY PlantManager can approve/reject
    if (isStandardPO && currentStatus === PurchaseOrderStatus.Submitted) {
      return isPlantManager;
    }

    // For Yarn POs that are Submitted: PurchaseOrderAdmin can approve/reject
    if (!isStandardPO && currentStatus === PurchaseOrderStatus.Submitted) {
      return AuthService.hasRole(PurchaseOrderRoles.PurchaseOrderAdmin);
    }

    // For Draft status: Admin/Editor can change to Submitted or Cancelled
    if (currentStatus === PurchaseOrderStatus.Draft) {
      return hasEditRole;
    }

    // For new PO (no status yet): Admin/Editor have control
    if (!currentStatus) {
      return hasEditRole;
    }

    // For any other status/type: no status changes allowed
    return false;
  }

  get workPurchaseOrderStatusOptions(): EnumValues {
    if (!this.enumValues || typeof this.enumValues !== 'object') return {};

    // Administrative POs: the Administrative role does everything a Standard PO splits between roles (#5942).
    if (this.isAdministrativePurchaseOrder) {
      return this.administrativeStatusOptions();
    }

    const hasEditRole =
      AuthService.hasRole(PurchaseOrderRoles.PurchaseOrderAdmin) || AuthService.hasRole(PurchaseOrderRoles.PurchaseOrderEditor);
    const isPlantManager = AuthService.hasRole(PlantManagerGroup.PlantManager);
    const isStandardPO = this.followsStandardFlow;
    const currentStatus = this.purchaseOrder?.status;

    // === CASE 1: Standard PO in Submitted status ===
    // ONLY PlantManager can approve/reject (Admin/Editor CANNOT)
    if (isStandardPO && currentStatus === PurchaseOrderStatus.Submitted) {
      if (!isPlantManager) {
        return {}; // Admin/Editor cannot approve Standard POs
      }
      // PlantManager can only approve or reject
      return {
        [PurchaseOrderStatus.Approved]: this.enumValues[PurchaseOrderStatus.Approved],
        [PurchaseOrderStatus.Rejected]: this.enumValues[PurchaseOrderStatus.Rejected],
      };
    }

    // === CASE 2: Yarn PO in Submitted status ===
    // PurchaseOrderAdmin can approve or reject
    if (!isStandardPO && currentStatus === PurchaseOrderStatus.Submitted) {
      if (!AuthService.hasRole(PurchaseOrderRoles.PurchaseOrderAdmin)) {
        return {};
      }
      return {
        [PurchaseOrderStatus.Approved]: this.enumValues[PurchaseOrderStatus.Approved],
        [PurchaseOrderStatus.Rejected]: this.enumValues[PurchaseOrderStatus.Rejected],
      };
    }

    // === CASE 3: Draft status ===
    // Admin/Editor can change to Submitted or Cancelled
    if (currentStatus === PurchaseOrderStatus.Draft && hasEditRole) {
      const options: EnumValues = {
        [PurchaseOrderStatus.Submitted]: this.enumValues[PurchaseOrderStatus.Submitted],
      };
      // Add Cancelled option if PO exists in DB
      if (!_.isNil(this.purchaseOrder?.id)) {
        options[PurchaseOrderStatus.Cancelled] = this.enumValues[PurchaseOrderStatus.Cancelled];
      }
      return options;
    }

    // === CASE 3: For any other case ===
    // No status changes allowed
    return {};
  }
  //add delete this comment

  get associatedRequisitionsInEntityKeyFormat(): EntityKey[] {
    return this.associatedRequisitions.map((req) => ({
      reference: req.id!,
      entityDefinitionCode: GlobalEntityDefinition.ExternalRequisition,
      number: req.number,
    }));
  }

  /**
   * Returns unique destination locations from associated requisitions.
   * Used to display destination warehouses when creating a new PO (before saving).
   */
  get destinationLocationsFromRequisitions(): { code: string; name: string }[] {
    const uniqueLocations = new Map<string, string>();

    for (const req of this.associatedRequisitions) {
      if (req.destinationLocationCode && !uniqueLocations.has(req.destinationLocationCode)) {
        const name = req.destinationLocationName?.en || req.destinationLocationName?.es || req.destinationLocationCode;
        uniqueLocations.set(req.destinationLocationCode, name);
      }
    }

    return Array.from(uniqueLocations.entries()).map(([code, name]) => ({ code, name }));
  }
  //#endregion

  private confirmationService = inject(ConfirmationService);
  private customerOrderProxyService = inject(CustomerOrderProxyService);
  private enumService = inject(EnumService);
  private invoiceservice = inject(InvoiceProxyService);
  private itemCategoryProxyService = inject(ItemCategoryProxyService);
  private itemSearchService = inject(ItemMasterProxyService);
  private messageService = inject(MessageService);
  private modalService = inject(ModalService);
  private businessProxyService = inject(BusinessProxyService);
  private purchaseOrderProxyService = inject(PurchaseOrderProxyService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private yarnSearchService = inject(YarnSearchProxyService);
  private userAuthenticationService = inject(UserAuthenticationService);
  private workOrderProxyService = inject(WorkOrderProxyService);
  private certificationProxyService = inject(CertificationProxyService);
  private exchangeRateProxyService = inject(ExchangeRateProxyService);
  private localeHelperService = inject(LocaleHelperService);
  private readonly quotationProxyService = inject(QuotationProxyService);
  private readonly masterDataProxyService = inject(MasterDataProxyService);
  private readonly requisitionProxyService = inject(RequisitionProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly clientPurchaseOrderProxyService = inject(ClientPurchaseOrderProxyService);
  private readonly locationProxyService = inject(LocationProxyService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  constructor() {
    effect(() => {
      this.uoms = this.enumService.getEnum('UnitOfMeasure', 'itemEnums')?.values;
      this.workOrderStatus = this.enumService.getEnum('WorkOrderStatus', 'orderEnums')?.values;
      this.currencyEnum = this.enumService.getEnum('Currency', 'financeEnums')?.values || {};
      this.workOrderShipmentTypeEnums = Object.values(this.enumService.getEnum('WorkOrderShipmentType', 'orderEnums')?.values || {});
      this.statusEnumObject = this.enumService.getEnum('PurchaseOrderStatus', 'orderEnums');
      this.enumValues = this.statusEnumObject?.values ?? {};
      this.orderTypeEnums = this.enumService.getEnum('PurchaseOrderType', 'orderEnums')?.values || {};
    });

    this.userLite = this.userAuthenticationService.userLite;
  }

  private administrativeStatusOptions(): EnumValues {
    if (!this.isAdministrativeAdmin) {
      return {};
    }
    const currentStatus = this.purchaseOrder?.status;
    if (currentStatus === PurchaseOrderStatus.Submitted) {
      return {
        [PurchaseOrderStatus.Approved]: this.enumValues[PurchaseOrderStatus.Approved],
        [PurchaseOrderStatus.Rejected]: this.enumValues[PurchaseOrderStatus.Rejected],
      };
    }
    if (currentStatus === PurchaseOrderStatus.Draft) {
      const options: EnumValues = {
        [PurchaseOrderStatus.Submitted]: this.enumValues[PurchaseOrderStatus.Submitted],
      };
      if (!_.isNil(this.purchaseOrder?.id)) {
        options[PurchaseOrderStatus.Cancelled] = this.enumValues[PurchaseOrderStatus.Cancelled];
      }
      return options;
    }
    return {};
  }

  //#region Permissions and Utilities
  canEdit() {
    // Administrative POs: only the Administrative role edits, and needs no Admin/Editor role besides (#5942).
    if (this.isAdministrativePurchaseOrder) {
      if (!this.isAdministrativeAdmin) {
        return false;
      }
      return this.purchaseOrder?.id ? this.purchaseOrder.status === PurchaseOrderStatus.Draft : true;
    }

    // Check role-based permissions first
    const hasEditRole =
      AuthService.hasRole(PurchaseOrderRoles.PurchaseOrderAdmin) || AuthService.hasRole(PurchaseOrderRoles.PurchaseOrderEditor);

    // PlantManager cannot edit fields, only approve/reject Standard POs when Submitted
    const isPlantManager = AuthService.hasRole(PlantManagerGroup.PlantManager);
    const isStandardPO = this.followsStandardFlow;
    const isCurrentlySubmitted = this.purchaseOrder?.status === PurchaseOrderStatus.Submitted;

    if (isPlantManager && isStandardPO && isCurrentlySubmitted && !hasEditRole) {
      return false; // PlantManager can only change status, not edit fields
    }

    if (!hasEditRole) {
      return false;
    }

    // If PO exists in DB, only allow editing if status is Draft
    if (this.purchaseOrder?.id) {
      return this.purchaseOrder.status === PurchaseOrderStatus.Draft;
    }

    // New PO (not saved yet) - allow editing
    return true;
  }

  private disableFormForNonDraftStatus(disableStatusControl = false): void {
    this.billToProvider.disable();
    this.billToAddress.disable();
    this.paymentMethod.disable();
    this.paymentTerms.disable();
    this.shipToProvider.disable();
    this.shipToAddress.disable();
    this.shippingMethod.disable();
    this.shippingTerms.disable();
    this.formControlWorkOrder.disable();
    this.formControlClientPO.disable();
    this.manufacturerSameAsProvider.disable();
    this.searchItemInput.disable();
    this.WorkOrderSelectedToSearch.disable();
    this.itemCategoryFilter.disable();
    this.provider.disable();
    this.providerAddress.disable();
    this.currency.disable();
    this.exchangeRateToUSD.disable();
    this.type.disable();
    this.items.controls.forEach((fg) => fg.disable());

    // Disable or enable status control based on user permissions
    if (disableStatusControl) {
      this.status.disable();
    } else {
      this.status.enable();
    }
  }

  setOptionsForPrimeNg(value: EnumValues) {
    if (!value) {
      return [];
    }
    return Object.keys(value).map((key) => {
      const numKey = parseInt(key);
      return {
        label: value[numKey],
        value: numKey,
      };
    });
  }
  //#endregion

  //#region Lifecycle and Initialization
  private isInitialLoad = true;

  ngOnInit(): void {
    this.loadConsumableWarehouses();
    this.getPurchaseOrder();
    this.getAllItemsCategories();
    this.setupFormSubscriptions();
    this.setupStatusChangeConfirmation();
    this.certificationProxyService.getAllCertifications().subscribe({
      next: (response: ApiResponse<Certification[]>) => {
        this.certifications = response.data ?? [];
      },
      error: () => {
        this.certifications = [];
      },
    });

    this.route.queryParams.subscribe(() => {
      const quotationDetails = this.route.snapshot.queryParamMap.get('quotationDetails');
      if (quotationDetails) {
        this.populateQuotationDetails(quotationDetails);
      }
    });

    //Disable form by Rol
    if (!this.canEdit()) {
      this.billToProvider.disable();
      this.billToAddress.disable();
      this.paymentMethod.disable();
      this.paymentTerms.disable();
      this.shipToProvider.disable();
      this.shipToAddress.disable();
      this.shippingMethod.disable();
      this.shippingTerms.disable();
      this.formControlWorkOrder.disable();
      this.manufacturerSameAsProvider.disable();
      this.searchItemInput.disable();
      this.WorkOrderSelectedToSearch.disable();
      this.itemCategoryFilter.disable();
    }
  }

  private setupStatusChangeConfirmation(): void {
    this.status.valueChanges.subscribe((newStatus) => {
      // Skip confirmation on initial load
      if (this.isInitialLoad) {
        return;
      }

      // Verify user has permission to change status
      if (!this.canChangeStatus) {
        // Revert to the original status
        const originalStatus = this.purchaseOrder?.status ?? PurchaseOrderStatus.Draft;
        this.status.setValue(originalStatus, { emitEvent: false });
        GlobalMessageUtil.warn(this.translateService.instant('Warning'), this.translateService.instant('No_Permission_To_Change_Status'));
        return;
      }

      // Show confirmation when changing from Draft to Submitted
      if (this.purchaseOrder?.status === PurchaseOrderStatus.Draft && newStatus === PurchaseOrderStatus.Submitted) {
        this.confirmationService.confirm({
          key: 'statusChange',
          header: this.translateService.instant('Purchase_Order_Status_Change_Confirm_Header'),
          message: this.translateService.instant('Purchase_Order_Status_Change_Draft_To_Submitted_Message'),
          icon: 'pi pi-exclamation-triangle',
          closable: false,
          acceptLabel: this.translateService.instant('Purchase_Order_Status_Change_Yes_Submit'),
          rejectLabel: this.translateService.instant('Cancel'),
          rejectButtonProps: { outlined: true },
          accept: () => {
            // Save the PO with the new Submitted status
            this.savePurchaseOrder();
          },
          reject: () => {
            // Revert status back to Draft
            this.status.setValue(PurchaseOrderStatus.Draft, { emitEvent: false });
          },
        });
      }

      // Show confirmation when changing from Submitted to Approved
      if (this.purchaseOrder?.status === PurchaseOrderStatus.Submitted && newStatus === PurchaseOrderStatus.Approved) {
        this.confirmationService.confirm({
          key: 'statusChange',
          header: this.translateService.instant('Purchase_Order_Status_Change_Confirm_Header'),
          message: this.translateService.instant('Purchase_Order_Status_Change_Submitted_To_Approved_Message'),
          icon: 'pi pi-exclamation-triangle',
          closable: false,
          acceptLabel: this.translateService.instant('Purchase_Order_Status_Change_Yes_Approve'),
          rejectLabel: this.translateService.instant('Cancel'),
          rejectButtonProps: { outlined: true },
          accept: () => {
            this.approvePurchaseOrder();
          },
          reject: () => {
            // Revert status back to Submitted
            this.status.setValue(PurchaseOrderStatus.Submitted, { emitEvent: false });
          },
        });
      }

      // Show confirmation when changing from Submitted to Rejected
      if (this.purchaseOrder?.status === PurchaseOrderStatus.Submitted && newStatus === PurchaseOrderStatus.Rejected) {
        this.confirmationService.confirm({
          key: 'statusChange',
          header: this.translateService.instant('Purchase_Order_Status_Change_Confirm_Header'),
          message: this.translateService.instant('Purchase_Order_Status_Change_Submitted_To_Rejected_Message'),
          icon: 'pi pi-exclamation-triangle',
          closable: false,
          acceptLabel: this.translateService.instant('Purchase_Order_Status_Change_Yes_Reject'),
          rejectLabel: this.translateService.instant('Cancel'),
          rejectButtonProps: { outlined: true },
          accept: () => {
            this.rejectPurchaseOrder();
          },
          reject: () => {
            // Revert status back to Submitted
            this.status.setValue(PurchaseOrderStatus.Submitted, { emitEvent: false });
          },
        });
      }

      // Show confirmation when changing to Cancelled
      if (newStatus === PurchaseOrderStatus.Cancelled) {
        const previousStatus = this.purchaseOrder?.status ?? PurchaseOrderStatus.Draft;

        this.confirmationService.confirm({
          key: 'statusChange',
          header: this.translateService.instant('Purchase_Order_Status_Cancel_Title'),
          message: this.translateService.instant('Purchase_Order_Status_Cancel_Message'),
          icon: 'pi pi-exclamation-circle',
          closable: false,
          acceptLabel: this.translateService.instant('Yes'),
          rejectLabel: this.translateService.instant('No'),
          acceptButtonStyleClass: 'p-button-danger',
          rejectButtonProps: { outlined: true },
          accept: () => {
            this.cancelPurchaseOrder();
          },
          reject: () => {
            // Revert status back to previous
            this.status.setValue(previousStatus, { emitEvent: false });
          },
        });
      }
    });
  }

  private approvePurchaseOrder(): void {
    if (!this.purchaseOrder?.id) {
      GlobalMessageUtil.error(this.translateService.instant('Error'), this.translateService.instant('Purchase_Order_Not_Found'));
      this.status.setValue(PurchaseOrderStatus.Submitted, { emitEvent: false });
      return;
    }

    this.isLoading++;
    this.purchaseOrderProxyService.changeStatus(this.purchaseOrder.id, PurchaseOrderStatus.Approved).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.success) {
          GlobalMessageUtil.success(
            this.translateService.instant('Purchase_Order_Approved_Title'),
            this.translateService.instant('Purchase_Order_Approved_Message', {
              poNumber: this.purchaseOrder?.purchaseOrderNumber,
            }),
          );
          // Refresh the page to reflect the new status
          this.router.navigate(['/purchase-orders']);
        } else {
          GlobalMessageUtil.error(this.translateService.instant('Error'), this.translateService.instant('Purchase_Order_Approve_Failed'));
          this.status.setValue(PurchaseOrderStatus.Submitted, { emitEvent: false });
        }
      },
      error: () => {
        this.isLoading--;
        this.status.setValue(PurchaseOrderStatus.Submitted, { emitEvent: false });
      },
    });
  }

  private rejectPurchaseOrder(): void {
    if (!this.purchaseOrder?.id) {
      GlobalMessageUtil.error(this.translateService.instant('Error'), this.translateService.instant('Purchase_Order_Not_Found'));
      this.status.setValue(PurchaseOrderStatus.Submitted, { emitEvent: false });
      return;
    }

    this.isLoading++;
    this.purchaseOrderProxyService.changeStatus(this.purchaseOrder.id, PurchaseOrderStatus.Rejected).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.success) {
          GlobalMessageUtil.success(
            this.translateService.instant('Purchase_Order_Rejected_Title'),
            this.translateService.instant('Purchase_Order_Rejected_Message', {
              poNumber: this.purchaseOrder?.purchaseOrderNumber,
            }),
          );
          // Refresh the page to reflect the new status
          this.router.navigate(['/purchase-orders']);
        } else {
          GlobalMessageUtil.error(this.translateService.instant('Error'), this.translateService.instant('Purchase_Order_Reject_Failed'));
          this.status.setValue(PurchaseOrderStatus.Submitted, { emitEvent: false });
        }
      },
      error: () => {
        this.isLoading--;
        this.status.setValue(PurchaseOrderStatus.Submitted, { emitEvent: false });
      },
    });
  }

  private cancelPurchaseOrder(): void {
    if (!this.purchaseOrder?.id) {
      GlobalMessageUtil.error(this.translateService.instant('Error'), this.translateService.instant('Purchase_Order_Not_Found'));
      this.status.setValue(PurchaseOrderStatus.Draft, { emitEvent: false });
      return;
    }

    this.isLoading++;
    this.purchaseOrderProxyService.changeStatus(this.purchaseOrder.id, PurchaseOrderStatus.Cancelled).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.success) {
          GlobalMessageUtil.success(
            this.translateService.instant('Purchase_Order_Cancelled_Title'),
            this.translateService.instant('Purchase_Order_Cancelled_Message', {
              poNumber: this.purchaseOrder?.purchaseOrderNumber,
            }),
          );
          // Navigate back to purchase orders list
          this.router.navigate(['/purchase-orders']);
        } else {
          GlobalMessageUtil.error(this.translateService.instant('Error'), this.translateService.instant('Purchase_Order_Cancel_Failed'));
          this.status.setValue(PurchaseOrderStatus.Draft, { emitEvent: false });
        }
      },
      error: () => {
        this.isLoading--;
        this.status.setValue(PurchaseOrderStatus.Draft, { emitEvent: false });
      },
    });
  }
  //#endregion

  //#region Currency and Price Calculations
  public getValueOnSelectedCurrency(value: number): number {
    const exchangeRate = this.purchaseOrder?.exchangeRateToUSD ?? this.exchangeRateToUSD.value;
    if (exchangeRate === null || exchangeRate === undefined || exchangeRate <= 0) {
      return value; // If exchange rate is not set, return the original value
    }
    return parseFloat((value * exchangeRate).toFixed(5));
  }

  public getValueOnUSD(value: number): number {
    const exchangeRate = this.purchaseOrder?.exchangeRateToUSD ?? this.exchangeRateToUSD.value;
    if (exchangeRate === null || exchangeRate === undefined || exchangeRate <= 0) {
      return value; // If exchange rate is not set, return the original value
    }
    return parseFloat((value / exchangeRate).toFixed(5));
  }

  /**
   * Initializes the entities array based on the order type.
   * For Yarn POs, adds Work Order option. For Chemical POs, adds Client Purchase Order option. For Standard POs, adds Quotation option.
   */
  private initializeEntitiesForOrderType(orderType: number): void {
    this.entities = [];
    if (orderType === PurchaseOrderType.Yarn) {
      this.drawerView = true;
      this.entities.push({
        label: 'Work Order',
        value: GlobalEntityDefinition.WorkOrder,
      });
    } else if (orderType === PurchaseOrderType.Chemicals) {
      this.entities.push({
        label: 'Client Purchase Order',
        value: CLIENT_PO_ENTITY_DEFINITION_CODE,
      });
    } else {
      this.entities.push({
        label: 'Quotation',
        value: GlobalEntityDefinition.Quotation,
      });
    }
  }

  private setupFormSubscriptions(): void {
    this.type.valueChanges.subscribe((type) => {
      if (type !== null) {
        this.initializeEntitiesForOrderType(type);
      }
    });

    this.billToProvider.valueChanges.subscribe((business) => {
      if (business && business.addresses) {
        this.billToAddresses = business.addresses
          .map((addr) => ({
            ...addr,
            displayName: `${addr.code || 'N/A'} - ${addr.address1}, ${addr.city}`,
          }))
          .filter((x) => x.addressTypes?.some((type) => type == 0)); // Filter address type 'BillING'

        // Auto-select if only one address is available
        // Bill To is read-only (#5969), so pick the company's billing address for the user.
        if (this.billToAddresses.length > 0) {
          this.billToAddress.setValue(this.billToAddresses[0]);
        }
      } else {
        this.billToAddresses = [];
        this.billToAddress.setValue(null);
        this.billToAddress.disable();
      }
    });

    this.shipToProvider.valueChanges.subscribe((business) => {
      if (business && business.addresses) {
        this.shipToAddresses = business.addresses
          .map((addr) => ({
            ...addr,
            displayName: `${addr.code || 'N/A'} - ${addr.address1}, ${addr.city}`,
          }))
          .filter((x) => x.addressTypes?.some((type) => type == 1)); // Filter address type 'SHIPPING'
        // Habilitar el campo de dirección cuando hay un provider seleccionado

        // Ship To is read-only (#5969), so pick the company's shipping address for the user.
        if (this.shipToAddresses.length > 0) {
          this.shipToAddress.setValue(this.shipToAddresses[0]);
        }
      } else {
        this.shipToAddresses = [];
        // Deshabilitar y limpiar el valor cuando no hay provider
        this.shipToAddress.setValue(null);
        this.shipToAddress.disable();
      }
    });

    this.provider.valueChanges.subscribe((business) => {
      if (business && business.addresses) {
        this.providerAddresses = business.addresses
          .map((addr) => ({
            ...addr,
            displayName: `${addr.code || 'N/A'} - ${addr.address1}, ${addr.city}`,
          }))
          .filter((x) => x.addressTypes?.some((type) => type == 0)); // Filter address type 'BILLING'
        this.providerAddress.enable();

        // Auto-select if only one address is available
        if (this.providerAddresses.length === 1) {
          this.providerAddress.setValue(this.providerAddresses[0]);
        }
      } else {
        this.providerAddresses = [];
        this.providerAddress.setValue(null);
        this.providerAddress.disable();
      }
    });
  }

  onSelectMaker(event: AutoCompleteSelectEvent) {
    this.manufacturers.push(event.value);
    this.manufacturer.setValue(null);
  }

  removeManufacturer(index: number) {
    this.manufacturers.splice(index, 1);
  }

  buildUniqueKey(yarnCode: string, workOrderReference: string, fabricCode: string): string {
    return `${yarnCode}-${workOrderReference}-${fabricCode}`;
  }

  getDistinctUoms(): number[] {
    const uoms = this.items.controls.map((item) => item.get('uom')?.value);
    return Array.from(new Set(uoms.filter((uom): uom is number => uom !== null && uom !== undefined))) ?? [];
  }

  getInvoiceTotalAmount(invoice: Invoice): string {
    // If invoice has totalAmount, use it directly
    if (invoice.totalAmount !== undefined && invoice.totalAmount !== null) {
      return invoice.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    // Otherwise, calculate from items including taxes
    const total = invoice.items.reduce((acc, invoiceItem) => {
      // Find the corresponding purchase order item to get price and tax info
      const poItem = this.items.controls.find((item) => item.controls.id.value === invoiceItem.itemInPurchaseOrderId);

      if (poItem) {
        const quantity = invoiceItem.quantity;
        const unitPrice = poItem.controls.unitPrice.value;
        const isTaxExempt = poItem.controls.isTaxExempt?.value ?? true;
        const taxRate = isTaxExempt ? 0 : 0.15;
        const discount = poItem.controls.discount?.value ?? 0;

        // Calculate item total: (quantity * (unitPrice + tax)) - discount
        const itemTotal = quantity * (unitPrice + unitPrice * taxRate) - discount;
        return acc + itemTotal;
      }

      return acc;
    }, 0);

    return total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  getTotalQuantityByUom(uom: number): string {
    const total = this.items.controls.reduce((acc, item) => {
      const itemUom = item.get('uom')?.value;
      const itemQuantity = item.get('quantity')?.value;
      return acc + (itemUom === uom ? (itemQuantity ?? 0) : 0);
    }, 0);
    return total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /** An Administrative order may only carry Services items (#5942); every other type takes any item. */
  private isItemAllowedForOrderType(item: ItemMaster): boolean {
    return !this.isAdministrativePurchaseOrder || item.itemClassificationCode === SERVICES_ITEM_CLASSIFICATION_CODE;
  }

  async getItemOptions(event: AutoCompleteCompleteEvent): Promise<void> {
    const term = event.query.trim();
    //if it starts with a numbers and has a dash liel 1-2 o 1002-2, we'll look for items by itemCode not by name
    const isCodePattern = /^\d+-\d+$/.test(term);

    const dataSearch: ItemSearchCommand = {
      itemCategoryCode: this.itemCategoryFilter.value?.code ?? '',
      name: isCodePattern ? '' : term, // Search by name if not code
      itemCode: isCodePattern ? term : '', // Search by itemCode if it matches pattern
    };
    if (this.isAdministrativePurchaseOrder) {
      dataSearch.itemClassificationCodes = [SERVICES_ITEM_CLASSIFICATION_CODE];
    }

    if (this.quotation && this.quotation.items) {
      //return the item looking by patern in quotaion item by code and name
      const itemCodesInQuotation = this.quotation.items.filter((itm) => itm.itemCode && new RegExp(term, 'i').test(itm.itemCode));
      const itemNameInQuotation = this.quotation.items.filter(
        (itm) =>
          itm.itemDisplayName &&
          ((itm.itemDisplayName.en && new RegExp(term, 'i').test(itm.itemDisplayName.en)) ||
            (itm.itemDisplayName.es && new RegExp(term, 'i').test(itm.itemDisplayName.es))),
      );
      const combinedItems = [...itemCodesInQuotation, ...itemNameInQuotation];
      const uniqueItemCodes = Array.from(new Set(combinedItems.map((item) => item.itemCode))).map((code) =>
        combinedItems.find((item) => item.itemCode === code)!,
      );

      const itemCodeBatchCommand = { itemCodes: uniqueItemCodes.map((item) => item.itemCode!) };

      this.itemSearchService.getItemCodesBatch(itemCodeBatchCommand).subscribe({
        next: (response: ApiResponse<ItemMaster[]>) => {
          const items = (response.data ?? []).filter((item) => this.isItemAllowedForOrderType(item));
          this.filteredItems = items.map((item) => ({
            ...item,
            fieldToDisplay: item.itemCode + ' ' + this.localeHelperService.getLocalizedValue(item.itemLongDisplayName),
          }));
        },
        error: () => {
          this.filteredItems = [];
        },
      });
      return;
    }

    try {
      const response = await lastValueFrom(
        this.itemSearchService.postItemSearch(dataSearch, true, this.followsStandardFlow).pipe(
          map((response: ApiResponse<PaginatedApiResponse<ItemMaster>>) => {
            const items = response.data?.items ?? [];
            const existingItemCodes = this.items.controls.map((control) => control.get('itemCode')?.value);

            return items
              .map((item) => ({
                ...item,
                fieldToDisplay: item.itemCode + ' ' + this.localeHelperService.getLocalizedValue(item.itemLongDisplayName),
              }))
              .filter((item) => !existingItemCodes.includes(item.itemCode) && this.isItemAllowedForOrderType(item));
          }),
          catchError(() => of([])),
        ),
      );

      this.filteredItems = response;
    } catch (error) {
      this.filteredItems = [];
      console.error('Error fetching items:', error);
    }
  }

  onSelectItem(event: AutoCompleteSelectEvent) {
    const itemMaster = event.value;

    // Si hay requisitions asociadas, solo permitir items que estén en la cotización
    if (this.associatedRequisitions.length > 0 && this.selectedQuotation) {
      const quotationItem = this.selectedQuotation.items.find((qi) => qi.itemCode === itemMaster.itemCode);
      if (!quotationItem) {
        GlobalMessageUtil.error(
          this.translateService.instant('Error'),
          this.translateService.instant('Purchase_Order_Item_Not_In_Quotation'),
        );
        this.searchItemInput.reset();
        return;
      }
      // Item está en la cotización, usar su precio y deshabilitar edición de precio
      this.items.push(
        this.createItemFormGroup(itemMaster, undefined, undefined, quotationItem.unitPrice, this.currentRequisitionOwner, true),
      );
    } else {
      // Sin requisitions asociadas, comportamiento normal
      this.items.push(this.createItemFormGroup(itemMaster, undefined, undefined, undefined, this.currentRequisitionOwner));
    }

    this.searchItemInput.reset();
  }

  /**
   * Returns a lightweight version of the purchase order, containing only essential fields.
   */
  buildPurchaseOrderLite(): Partial<PurchaseOrder> {
    return {
      id: this.purchaseOrder?.id,
      status: this.status.value,
      purchaseOrderNumber: this.purchaseOrder?.purchaseOrderNumber ?? '',
      items: this.items.controls.map((item) => ({
        id: item.controls.id.value,
        itemCode: item.controls.itemCode.value,
        quantity: item.controls.quantity.value,
        uom: Number(item.controls.uom.value),
        unitPrice: item.controls.unitPrice.value,
        totalPrice: item.controls.totalPrice.value,
        discount: item.controls.discount?.value ?? undefined,
        taxRate: item.controls.isTaxExempt.value ? 0 : 0.15,
      })),
    };
  }

  openmodalpurchaseorder() {
    // invoice.purchaseOrders = this.buildPurchaseOrderLite() as PurchaseOrder;
    // this.modalService.open(InvoiceDetailModalComponent, {}, { invoice: invoice ?? null }).subscribe((result) => {});
  }

  viewFile(presignedUrl: string) {
    window.open(presignedUrl, '_blank');
  }

  getFileName(nameWithPrefix: string): string {
    const lastSlashIndex = nameWithPrefix.lastIndexOf('/');
    if (lastSlashIndex === -1) {
      return nameWithPrefix;
    }
    return nameWithPrefix.substring(lastSlashIndex + 1);
  }

  // Métodos para Bill To
  async billToProviderChange(event: AutoCompleteCompleteEvent): Promise<void> {
    const text = event.query.trim();

    const searchCommand: BusinessSearchCommand = {
      name: text,
      code: text,
      businessTypes: [BusinessEntityType.InternalCompany],
    };

    this.businessProxyService.search(searchCommand, true).subscribe({
      next: (response) => {
        const businesses = response.data?.items ?? [];
        this.billToProviders = businesses.map((business) => ({
          ...business,
          fieldToDisplay: (business.code || '') + ' ' + business.name,
        }));
      },
      error: () => {
        this.billToProviders = [];
      },
    });
  }

  async shipToProviderChange(event: AutoCompleteCompleteEvent): Promise<void> {
    const text = event.query.trim();

    const searchCommand: BusinessSearchCommand = {
      name: text,
      code: text,
      businessTypes: [BusinessEntityType.InternalCompany],
    };

    this.businessProxyService.search(searchCommand, true).subscribe({
      next: (response) => {
        const businesses = response.data?.items ?? [];
        this.shipToProviders = businesses.map((business) => ({
          ...business,
          fieldToDisplay: (business.code || '') + ' ' + business.name,
        }));
      },
      error: () => {
        this.shipToProviders = [];
      },
    });
  }

  async searchMaker(event: AutoCompleteCompleteEvent): Promise<void> {
    const text = event.query.trim();

    const searchCommand: BusinessSearchCommand = {
      name: text,
      code: text,
      businessTypes: [BusinessEntityType.Manufacturer],
    };

    this.businessProxyService.search(searchCommand, true).subscribe({
      next: (response) => {
        const makers = response.data?.items ?? [];
        this.makersToDisplay = makers
          .filter((maker) => maker.id !== this.referenceMakerOriginal)
          .slice(0, 8)
          .map((maker) => ({
            ...maker,
            fieldToDisplay: (maker.code || '') + ' ' + maker.name,
          }));
      },
      error: () => {
        this.makersToDisplay = [];
      },
    });
  }

  private async getFilesFromDropEntries(files: NgxFileDropEntry[]): Promise<File[]> {
    const filePromises: Promise<File>[] = [];

    for (const droppedFile of files) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;

        const filePromise = new Promise<File>((resolve, reject) => {
          fileEntry.file(resolve, reject);
        });

        filePromises.push(filePromise);
      }
    }

    return await Promise.all(filePromises);
  }

  async uploadFiles(files: NgxFileDropEntry[]): Promise<void> {
    try {
      const fileList = await this.getFilesFromDropEntries(files);

      if (!this.purchaseOrder?.id) return;

      const uploadedFiles = await lastValueFrom(this.purchaseOrderProxyService.uploadFiles(fileList, this.purchaseOrder.id));

      if (_.isNil(this.purchaseOrder.files)) {
        this.purchaseOrder.files = [];
      }

      this.purchaseOrder.files.push(...uploadedFiles);

      GlobalMessageUtil.success(this.translateService.instant('Success'), this.translateService.instant('Purchase_Order_Files_Uploaded'));
    } catch (error) {
      console.error(error);
    }
  }

  deleteFile(file: S3Object): void {
    if (!file) return;

    console.log(file);

    this.purchaseOrderProxyService.deleteFile(this.purchaseOrder!.id, file).subscribe({
      next: (resp) => {
        console.log(resp);
        if (resp && resp.success) {
          if (!this.purchaseOrder!.files) return;
          GlobalMessageUtil.success(
            this.translateService.instant('Success'),
            this.translateService.instant('PurchaseOrderDetailPage_Messages.FilesDeletedSuccessfully'),
          );
          this.purchaseOrder!.files = this.purchaseOrder!.files.filter((x) => x.presignedUrl !== file.presignedUrl);
        }
      },
      error: (e) => {
        console.error(e);
      },
    });
  }

  public getCustomerOrderOptions(event: AutoCompleteCompleteEvent): void {
    const text = event.query.trim();
    if (!text) {
      this.filteredCustomerOrders = [];
      return;
    }

    this.customerOrderProxyService
      .searchCustomerOrder({ orderNumber: text })
      .pipe(catchError(() => of([])))
      .subscribe((response: ApiResponse<CustomerOrder[]> | never[]) => {
        if (Array.isArray(response)) {
          this.filteredCustomerOrders = response;
        } else if (response?.data) {
          this.filteredCustomerOrders = response.data;
        } else {
          this.filteredCustomerOrders = [];
        }
      });
  }

  getUnitName(uomKey: number | null): string {
    if (uomKey === null || uomKey === undefined) return '';
    return this.uoms[uomKey] || '';
  }

  async providerChange(event: AutoCompleteCompleteEvent): Promise<void> {
    const text = event.query.trim();

    const searchCommand: BusinessSearchCommand = {
      name: text,
      code: text,
      businessTypes: [BusinessEntityType.Provider],
    };

    this.businessProxyService.search(searchCommand, true).subscribe({
      next: (response) => {
        const providers = response.data?.items ?? [];

        this.providers = providers.map((provider) => ({
          ...provider,
          fieldToDisplay: provider.code + ' ' + provider.name,
        }));
      },
      error: () => {
        this.providers = [];
      },
    });
  }

  private loadConsumableWarehouses(): void {
    this.locationProxyService.getConsumableWarehouses().subscribe({
      next: (response) => {
        if (response.data) {
          this.destinationLocationOptions = response.data.map((w) => ({
            value: w.locationCode,
            label: this.localeHelperService.getLocalizedValue(w.fullName ?? w.name) + ` (${w.locationCode})`,
          }));
        }
      },
    });
  }

  private getPurchaseOrder(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['id']) {
        if (params['id'] === 'new') {
          this.breadcrumbService.setCurrent('Common_New');
          this.defaultBillAndShipToSiteBusiness();
          // Set order type from query parameter if provided
          if (params['orderType'] !== undefined && params['orderType'] !== null) {
            const orderType = Number(params['orderType']);
            // The API refuses the save anyway; refusing here spares the user a form they cannot submit (#5942).
            if (
              orderType === PurchaseOrderType.Administrative &&
              !AuthService.hasRole(AdministrativePurchaseOrderRoles.AdministrativePurchaseOrderAdmin)
            ) {
              GlobalMessageUtil.error(
                this.translateService.instant('Error'),
                this.translateService.instant('Purchase_Order_Administrative_Role_Required'),
              );
              this.router.navigate(['/purchase-orders']);
              return;
            }
            this.type.setValue(orderType, { emitEvent: false });
            this.type.disable();
            // Initialize entities array based on order type (since emitEvent: false doesn't trigger valueChanges)
            this.initializeEntitiesForOrderType(orderType);
          }
          // Check if we're copying from another PO
          if (params['copyFromPoReference']) {
            this.loadPurchaseOrderForCopy(params['copyFromPoReference']);
            return;
          }

          const quotationReference = this.route.snapshot.queryParamMap.get('quotationReference');
          if (_.isNil(quotationReference)) {
            return;
          }
        }

        this.purchaseOrderProxyService.getPurchaseOrderV2(params['id']).subscribe({
          next: (order: PurchaseOrder | null) => {
            if (order) {
              this.purchaseOrder = order;
              this.breadcrumbService.setCurrent(order.purchaseOrderNumber);
              const relatedEntities = order.relatedEntities ?? [];
              if (!_.isEmpty(relatedEntities)) {
                this.formControlEntity.setValue(relatedEntities[0].entityDefinitionCode, { emitEvent: false });
                this.formControlEntity.disable();
              }

              this.type.setValue(order.type);

              // Set currency and exchange rate from loaded PO
              this.currency.setValue(order.currency ?? 0, { emitEvent: false });
              if (order.exchangeRateToUSD) {
                this.exchangeRateToUSD.setValue(order.exchangeRateToUSD, { emitEvent: false });
              }

              this.notes = order.notes ?? [];
              if (order.paymentMethodCode) {
                this.paymentMethod.setValue(order.paymentMethodCode, { emitEvent: false });
              }
              if (order.paymentTermCode) {
                this.paymentTerms.setValue(order.paymentTermCode, { emitEvent: false });
              }
              // Si tienes shippingMethodCode en tu modelo:
              if (order.shippingMethodCode != null) {
                this.shippingMethod.setValue(order.shippingMethodCode, { emitEvent: false });
              }
              if (order.shippingTerm !== undefined) {
                this.shippingTerms.setValue(order.shippingTerm, { emitEvent: false });
              }

              // Set destination location code from saved PO (not editable once created)
              if (order.destinationLocationCodes && order.destinationLocationCodes.length > 0) {
                this.destinationLocationCode.setValue(order.destinationLocationCodes[0], { emitEvent: false });
              }
              this.destinationLocationCode.disable();

              if (order.billToReference) {
                this.loadBillToProvider(order.billToReference);
              }
              if (order.shipToReference) {
                this.loadShipToProvider(order.shipToReference);
              }
              this.searchRelatedEntities(order.relatedEntities);
              // Filter out cancelled invoices (status = 4)
              this.invoices = (order.invoices ?? []).filter((invoice) => invoice.status !== 4);

              if (order.provider) {
                order.provider.fieldToDisplay = `(${order.provider.code}) ${order.provider.name}`;
                this.providers.push(order.provider);
                this.provider.setValue(order.provider, {
                  emitEvent: false,
                });
                this.provider.disable();
              }
              if (order.manufacturers) {
                if (order.manufacturers.length === 1 && order.manufacturers[0].id === order.provider?.id) {
                  this.manufacturerSameAsProvider.setValue(true, { emitEvent: false });
                } else {
                  this.manufacturers = order.manufacturers.map((m) => ({
                    ...m,
                    fieldToDisplay: `(${m.code}) ${m.name}`,
                  }));
                }
              }

              // Guardar referencia del provider original para evitar duplicados en maker
              this.referenceMakerOriginal = order.provider?.id ?? '';

              this.status.setValue(order.status);
              this.isInitialLoad = false;

              // Disable form if PO is not in Draft status
              if (!this.canEdit()) {
                // Use the canChangeStatus getter to determine if status control should be enabled
                this.disableFormForNonDraftStatus(!this.canChangeStatus);
              }

              // Load saved dates if present, otherwise keep today's date
              if (order.issuedDate) {
                const d = new Date(order.issuedDate);
                if (!isNaN(d.getTime())) this.issuedDate = d;
              }
              if (order.quotedDate) {
                const q = new Date(order.quotedDate);
                if (!isNaN(q.getTime())) this.quotedDate = q;
              }
              if (order.items) this.fetchItems(order.items);

              // Cargar datos de Bill To y Ship To si existen
              if (order.billToReference) {
                // Aquí cargarías el provider del billTo
                this.loadBillToProvider(order.billToReference);
              }
              if (order.shipToReference) {
                // Aquí cargarías el provider del shipTo
                this.loadShipToProvider(order.shipToReference);
              }
            }
          },
          error: (error) => {
            console.error('Error al hacer la solicitud: ', error);
          },
        });
      }
    });
  }

  private populateQuotationForm(quotation: Quotation, requisition: ExternalRequisition): void {
    this.businessProxyService.getById(quotation.providerReference!).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const provider = response.data;
          provider.fieldToDisplay = `${provider.code} ${provider.name}`;
          this.providers.push(provider);
          this.provider.setValue(provider, { emitEvent: false });
          this.provider.disable();
        }
      },
    });

    // Guardar quotation en la nueva estructura
    this.selectedQuotation = quotation;
    this.quotation = quotation; // Mantener legacy para compatibilidad

    this.type.setValue(0, { emitEvent: false }); // Assuming 0 is the default type for quotations
    this.type.disable();
    // Crear EntityKey para la requisition que será el owner de todos los items
    const requisitionOwner: EntityKey = {
      entityDefinitionCode: GlobalEntityDefinition.ExternalRequisition,
      reference: requisition.id!,
      number: requisition.number,
    };

    // Establecer como currentRequisitionOwner para futuros items
    this.currentRequisitionOwner = requisitionOwner;

    //populate in items form array
    quotation.items.forEach((itm) => {
      const reqItem = requisition.entries!.find((ri) => ri.itemCode === itm.itemCode);

      const item: Partial<ItemMaster> = {
        itemCode: itm.itemCode,
        itemLongDisplayName: itm.itemDisplayName,
        fieldToDisplay: itm.itemCode + ' ' + (itm.itemDisplayName?.en ?? ''),
      };
      // Asignar requisitionOwner a cada item
      this.items.push(
        this.createItemFormGroup(item as ItemMaster, undefined, reqItem?.quantity, itm.unitPrice, requisitionOwner, true, reqItem?.uom),
      );
    });

    // Guardar requisition en la nueva estructura
    this.associatedRequisitions.push(requisition);
    this.externalRequisition = requisition; // Mantener legacy para compatibilidad
  }

  private populateQuotationDetails(quotationReference: string): void {
    this.isLoading++;

    this.quotationProxyService.getById(quotationReference).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.success && response.data) {
          const quotation = response.data;

          this.currency.setValue(quotation.currency);
          this.exchangeRateToUSD.setValue(quotation.exchangeRateToUSD);
          this.currency.disable();

          if (_.isNil(quotation.providerReference)) {
            GlobalMessageUtil.error(
              this.translateService.instant('Error'),
              this.translateService.instant('Purchase_Order_Quotation_Provider_Missing'),
            );
            this.router.navigate(['/purchase-orders']);
            return;
          } else {
            this.requisitionProxyService.getRequisitionById(quotation.initiator.reference).subscribe({
              next: (requisitionResponse) => {
                if (requisitionResponse.success && requisitionResponse.data) {
                  this.externalRequisition = requisitionResponse.data;
                  this.quotation = quotation;
                  this.populateQuotationForm(quotation, requisitionResponse.data);
                }
              },
            });
          }
        }
      },
      error: () => {
        this.isLoading--;
        this.router.navigate(['/purchase-orders']);
      },
    });
  }

  private loadPurchaseOrderForCopy(poReference: string): void {
    this.purchaseOrderProxyService.getPurchaseOrderV2(poReference).subscribe({
      next: (order: PurchaseOrder | null) => {
        if (order) {
          // Don't set purchaseOrder - we want to create a new one
          // But store the original PO number for the note
          const originalPoNumber = order.purchaseOrderNumber;

          const relatedEntities = order.relatedEntities ?? [];
          if (!_.isEmpty(relatedEntities)) {
            this.formControlEntity.setValue(relatedEntities[0].entityDefinitionCode, { emitEvent: false });
          }

          if (order.exchangeRateToUSD) {
            this.exchangeRateToUSD.setValue(order.exchangeRateToUSD, { emitEvent: false });
          }

          // Add a note about the copy
          const copyNote: Note = {
            id: new ObjectId().toHexString(),
            richText: `This PO was created from a copy of ${originalPoNumber}`,
            userLite: this.userLite!,
            dateModified: new Date(),
          };
          this.notes = [copyNote];

          if (order.paymentMethodCode) {
            this.paymentMethod.setValue(order.paymentMethodCode, { emitEvent: false });
          }
          if (order.paymentTermCode) {
            this.paymentTerms.setValue(order.paymentTermCode, { emitEvent: false });
          }
          if (order.shippingMethodCode != null) {
            this.shippingMethod.setValue(order.shippingMethodCode, { emitEvent: false });
          }
          if (order.shippingTerm !== undefined) {
            this.shippingTerms.setValue(order.shippingTerm, { emitEvent: false });
          }

          if (order.billToReference) {
            this.loadBillToProvider(order.billToReference, order.billToAddressCode);
          }

          if (order.shipToReference) {
            this.loadShipToProvider(order.shipToReference, order.shipToAddressCode);
          }

          this.searchRelatedEntities(order.relatedEntities);

          if (order.provider) {
            // Load provider with fresh data from API (similar to billTo/shipTo)
            this.loadProviderForCopy(order.provider.id!, order.providerAddressCode);
          }

          if (order.manufacturers) {
            if (order.manufacturers.length === 1 && order.manufacturers[0].id === order.provider?.id) {
              this.manufacturerSameAsProvider.setValue(true, { emitEvent: false });
            } else {
              this.manufacturers = order.manufacturers.map((m) => ({
                ...m,
                fieldToDisplay: `(${m.code}) ${m.name}`,
              }));
            }
          }

          this.referenceMakerOriginal = order.provider?.id ?? '';

          this.status.setValue(0); // Set to Draft for new PO
          this.isInitialLoad = false;
          this.currency.setValue(order.currency ?? 0, { emitEvent: false });

          // Use current date for new PO
          this.issuedDate = new Date();
          this.quotedDate = new Date();

          // Copy invoices from original PO (filter out cancelled invoices - status = 4)
          this.invoices = (order.invoices ?? []).filter((invoice) => invoice.status !== 4);

          // Copy shipments from original PO (filter out cancelled shipments - status = 7)
          this.shipments = (order.shipments ?? []).filter((shipment) => shipment.status !== 7);

          // Fetch items with original PO items data (for quantity, price, etc.)
          if (order.items) this.fetchItems(order.items, order.items);
        }
      },
      error: (error) => {
        console.error('Error loading purchase order for copy: ', error);
      },
    });
  }

  private getAllItemsCategories(): void {
    this.itemCategoryProxyService.getAllItemsCategories().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.itemsCategories.set(
            response.data
              .filter((category) => category.level === HierarchyLevel.Root)
              .map((category) => ({ ...category, fieldToDisplay: this.localeHelperService.getLocalizedValue(category.name) })),
          );
        }
      },
    });
  }

  /**
   * A new PO bills and ships to the company the current site operates under: NT on NT, PC on PC (#5969). Each
   * field is set only while still empty, so a copied PO is never overwritten. Emits, so the company's billing and
   * shipping addresses are selected too. A site with no company leaves both empty, and saving then warns.
   */
  private defaultBillAndShipToSiteBusiness(): void {
    this.businessProxyService.getSiteBusiness().subscribe({
      next: (response) => {
        const business = response.data;
        if (_.isNil(business)) {
          return;
        }
        const siteBusiness = { ...business, fieldToDisplay: (business.code || '') + ' ' + business.name };
        if (_.isNil(this.billToProvider.value)) {
          this.billToProvider.setValue(siteBusiness);
        }
        if (_.isNil(this.shipToProvider.value)) {
          this.shipToProvider.setValue(siteBusiness);
        }
      },
      error: () => {
        // Both stay empty; saving warns that the site has no company set up.
      },
    });
  }

  private loadBillToProvider(billToReference: string, addressCode?: string): void {
    this.isLoading++;
    this.businessProxyService.getById(billToReference).subscribe({
      next: (business) => {
        if (!_.isNil(business.data)) {
          const provider = {
            ...business.data,
            fieldToDisplay: (business.data.code || '') + ' ' + business.data.name,
          };
          this.billToProvider.setValue(provider, { emitEvent: false });

          // ✅ CORREGIR: Buscar la dirección específica por código
          // let selectedAddress = null;
          // const billToAddressCode = addressCode || this.purchaseOrder?.billToAddressCode;

          // if (this.purchaseOrder?.billToAddressCode && business.data.addresses) {
          //   selectedAddress = business.data.addresses.find(
          //     (addr) => addr.addressTypes?.includes(0) && addr.code?.toString() === this.purchaseOrder?.billToAddressCode?.toString(),
          //   );
          // }

          let selectedAddress = null;
          const billToAddressCode = addressCode || this.purchaseOrder?.billToAddressCode;
          if (billToAddressCode && business.data.addresses) {
            selectedAddress = business.data.addresses.find(
              (addr) => addr.addressTypes?.includes(0) && addr.code?.toString() === billToAddressCode?.toString(),
            );
          }

          // Si no se encuentra la específica, usar la primera con tipo correcto (como fallback)
          if (!selectedAddress && business.data.addresses) {
            selectedAddress = business.data.addresses.find((addr) => addr.addressTypes?.includes(0));
          }

          // Agregar displayName si se encuentra la dirección
          if (selectedAddress) {
            selectedAddress = {
              ...selectedAddress,
              displayName: `${selectedAddress.code || 'N/A'} - ${selectedAddress.address1}, ${selectedAddress.city}`,
            };
          }

          // The select only labels a value it finds among its options, so a saved PO needs them too (#5969).
          this.billToAddresses = selectedAddress ? [selectedAddress] : [];
          this.billToAddress.setValue(selectedAddress ?? null, { emitEvent: false });
          this.isLoading--;
        }
      },
      error: () => {
        this.isLoading--;
        this.billToProvider.setValue(null, { emitEvent: false });
        this.billToAddress.setValue(null, { emitEvent: false });
        this.billToProvider.disable();
        this.billToAddress.disable();
      },
    });
  }

  private loadShipToProvider(shipToReference: string, addressCode?: string): void {
    this.isLoading++;
    this.businessProxyService.getById(shipToReference).subscribe({
      next: (business) => {
        if (!_.isNil(business.data)) {
          const provider = {
            ...business.data,
            fieldToDisplay: (business.data.code || '') + ' ' + business.data.name,
          };
          this.shipToProvider.setValue(provider, { emitEvent: false });

          // let selectedAddress = null;
          // if (this.purchaseOrder?.shipToAddressCode && business.data.addresses) {
          //   selectedAddress = business.data.addresses.find(
          //     (addr) => addr.addressTypes?.includes(1) && addr.code?.toString() === this.purchaseOrder?.shipToAddressCode?.toString(),
          //   );
          // }

          let selectedAddress = null;
          const shipToAddressCode = addressCode || this.purchaseOrder?.shipToAddressCode;
          if (shipToAddressCode && business.data.addresses) {
            selectedAddress = business.data.addresses.find(
              (addr) => addr.addressTypes?.includes(1) && addr.code?.toString() === shipToAddressCode?.toString(),
            );
          }

          if (!selectedAddress && business.data.addresses) {
            selectedAddress = business.data.addresses.find((addr) => addr.addressTypes?.includes(1));
          }

          if (selectedAddress) {
            selectedAddress = {
              ...selectedAddress,
              displayName: `${selectedAddress.code || 'N/A'} - ${selectedAddress.address1}, ${selectedAddress.city}`,
            };
          }

          // The select only labels a value it finds among its options, so a saved PO needs them too (#5969).
          this.shipToAddresses = selectedAddress ? [selectedAddress] : [];
          this.shipToAddress.setValue(selectedAddress ?? null, { emitEvent: false });
          this.isLoading--;
        }
      },
      error: () => {
        this.isLoading--;
        this.shipToProvider.setValue(null, { emitEvent: false });
        this.shipToAddress.setValue(null, { emitEvent: false });
        this.shipToProvider.disable();
        this.shipToAddress.disable();
      },
    });
  }

  /**
   * Loads provider data from API for copy flow to ensure complete address data
   */
  private loadProviderForCopy(providerReference: string, addressCode?: string): void {
    this.isLoading++;
    this.businessProxyService.getById(providerReference).subscribe({
      next: (business) => {
        if (!_.isNil(business.data)) {
          const provider = {
            ...business.data,
            fieldToDisplay: `(${business.data.code}) ${business.data.name}`,
          };
          this.providers.push(provider);
          this.provider.setValue(provider, { emitEvent: false });

          if (this.canEdit()) {
            this.provider.enable();
          } else {
            this.provider.disable();
          }

          // Populate provider addresses with complete data from API
          if (business.data.addresses) {
            this.providerAddresses = business.data.addresses
              .filter((addr) => addr.addressTypes?.some((type) => type === 0))
              .map((addr) => ({
                ...addr,
                displayName: `${addr.code || 'N/A'} - ${addr.address1}, ${addr.city}`,
              }));
          }

          // Find specific address by code
          let selectedAddress = null;
          if (addressCode && business.data.addresses) {
            selectedAddress = business.data.addresses.find(
              (addr) => addr.addressTypes?.includes(0) && addr.code?.toString() === addressCode?.toString(),
            );
          }

          // Fallback: use first billing address if specific not found
          if (!selectedAddress && business.data.addresses) {
            selectedAddress = business.data.addresses.find((addr) => addr.addressTypes?.includes(0));
          }

          // Add displayName if address found
          if (selectedAddress) {
            selectedAddress = {
              ...selectedAddress,
              displayName: `${selectedAddress.code || 'N/A'} - ${selectedAddress.address1}, ${selectedAddress.city}`,
            };
          }

          this.providerAddress.setValue(selectedAddress ?? null, { emitEvent: false });
          if (this.canEdit()) {
            this.providerAddress.enable();
          } else {
            this.providerAddress.disable();
          }
          this.isLoading--;
        }
      },
      error: () => {
        this.isLoading--;
        this.provider.setValue(null, { emitEvent: false });
        this.providerAddress.setValue(null, { emitEvent: false });
        this.provider.disable();
        this.providerAddress.disable();
      },
    });
  }

  private fetchItems(items: PurchaseOrderItem[], originalItems?: PurchaseOrderItem[]): void {
    this.isLoading++;

    const command = {
      itemCodes: items.map((item) => item.itemCode),
    };

    // Use originalItems if provided (for copy flow), otherwise use this.purchaseOrder?.items
    const sourceItems = originalItems ?? this.purchaseOrder?.items ?? [];

    this.itemSearchService.getItemCodesBatch(command).subscribe({
      next: (response) => {
        const fetchedItems = response.data ?? [];

        // Iterate over sourceItems (not fetchedItems) to preserve duplicate itemCodes
        // with different owners (e.g., multiple external requisitions for the same item)
        sourceItems.forEach((poItem) => {
          const itemMaster = fetchedItems.find((fi) => fi.itemCode === poItem.itemCode);
          if (itemMaster) {
            this.items.push(this.createItemFormGroup(itemMaster, poItem));
          }
        });

        this.isLoading--;
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  private getUSDPrice(price: number): number {
    const exchangeRate = this.exchangeRateToUSD.value;
    if (exchangeRate && exchangeRate > 0) {
      return price / exchangeRate;
    }
    return price; // If no exchange rate, assume price is already in USD
  }

  private createItemFormGroup(
    item: ItemMaster,
    purchaseOrderItem?: PurchaseOrderItem,
    quantity?: number,
    unitPrice?: number,
    owner?: EntityKey | null,
    disablePrice = false,
    overrideUom?: number,
  ): FormGroup<ItemFormGroup> {
    // Determine the price to use - prices are stored in the selected currency
    let priceToUse = unitPrice ?? purchaseOrderItem?.unitPrice ?? 0;

    // Handle special case for overrideUom (Quotation scenario)
    if (overrideUom) {
      priceToUse = this.getUSDPrice(priceToUse);
    }

    // Prices are stored in the selected currency, so use them as-is
    const displayPrice = priceToUse;
    const displayQuantity = quantity ?? purchaseOrderItem?.quantity ?? 0;
    // Determine if tax exempt based on taxRate from purchaseOrderItem
    const isTaxExempt = _.isNil(purchaseOrderItem?.taxRate) || purchaseOrderItem?.taxRate === 0;
    const taxRate = isTaxExempt ? 0 : (purchaseOrderItem?.taxRate ?? 0);
    const totalPrice = displayQuantity * (displayPrice + displayPrice * taxRate) - (purchaseOrderItem?.discount ?? 0);
    const fg = new FormGroup<ItemFormGroup>({
      id: new FormControl(purchaseOrderItem?.id ?? new ObjectId().toHexString(), { nonNullable: true }),
      itemCode: new FormControl(item.itemCode, { nonNullable: true }),
      longDescription: new FormControl(this.localeHelperService.getLocalizedValue(item.itemLongDisplayName)),
      quantity: new FormControl(purchaseOrderItem?.quantity ?? quantity ?? 0, {
        validators: [Validators.required, Validators.min(0.01)],
        nonNullable: true,
      }),

      uom: new FormControl(overrideUom ?? item.unitOfMeasure),
      unitPrice: new FormControl(
        {
          value: displayPrice,
          disabled: disablePrice,
        },
        {
          validators: [Validators.required, Validators.min(0.000001)],
          nonNullable: true,
        },
      ),
      isTaxExempt: new FormControl(isTaxExempt, { nonNullable: true }),
      totalPrice: new FormControl(
        {
          value: totalPrice,
          disabled: true,
        },
        {
          nonNullable: true,
        },
      ),
      discount: new FormControl(purchaseOrderItem?.discount ?? 0),
      owner: new FormControl(purchaseOrderItem?.owner ?? owner ?? null),
    });

    combineLatest([
      fg.controls.quantity.valueChanges.pipe(startWith(fg.controls.quantity.value)),
      fg.controls.unitPrice.valueChanges.pipe(startWith(fg.controls.unitPrice.value)),
      fg.controls.discount?.valueChanges.pipe(startWith(fg.controls.discount.value)),
      fg.controls.isTaxExempt.valueChanges.pipe(startWith(fg.controls.isTaxExempt.value)),
    ])
      .pipe(
        map(([qty, price, disc, taxExempt]) => {
          const tax = taxExempt ? 0 : 0.15; // 15% tax rate when not exempt
          return (qty ?? 0) * ((price ?? 0) + (price ?? 0) * tax) - (disc ?? 0);
        }),
      )
      .subscribe((total) => fg.controls.totalPrice.setValue(total, { emitEvent: false }));

    // Sync isTaxExempt across all rows with the same ItemCode
    fg.controls.isTaxExempt.valueChanges.subscribe((taxExempt) => {
      const currentItemCode = fg.controls.itemCode.value;
      if (currentItemCode) {
        this.items.controls.forEach((itemFormGroup) => {
          const otherItemCode = itemFormGroup.controls.itemCode.value;
          if (otherItemCode === currentItemCode && itemFormGroup !== fg) {
            itemFormGroup.controls.isTaxExempt.setValue(taxExempt, { emitEvent: false });
          }
        });
      }
    });

    if (!this.canEdit()) {
      fg.disable();
    }
    return fg;
  }

  deleteItemArrayForm(index: number) {
    this.items.removeAt(index);
  }

  private fetchInvoices(invoiceReference: string): void {
    this.isLoading++;
    this.invoiceservice.getInvoicesPurchaseOrderNumber(invoiceReference).subscribe({
      next: (invoices) => {
        // Filter out null invoices and cancelled invoices (status = 4)
        this.invoices = Array.isArray(invoices.data)
          ? invoices.data.filter((item): item is Invoice => item !== null && item.status !== 4)
          : [invoices.data].filter((item): item is Invoice => item !== null && item.status !== 4);
        this.isLoading--;
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  formatDate(date: Date | string): string {
    if (!date) return '';

    const parsedDate = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(parsedDate.getTime())) return '';

    return `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, '0')}-${String(parsedDate.getDate()).padStart(2, '0')}`;
  }

  fetchCurrencyExchangeRateToUSD(): void {
    if (this.currency.value === 0) {
      this.exchangeRateToUSD.setValue(null, { emitEvent: false });
      return;
    }

    this.isLoading++;
    this.exchangeRateProxyService.getByCurrency(this.currency.value).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.data && response.data.rateToUSD) {
          this.exchangeRateToUSD.setValue(response.data.rateToUSD, { emitEvent: false });
        } else {
          GlobalMessageUtil.warn(
            'Purchase Order: Warning',
            'No exchange rate found for the selected currency. Please enter the exchange rate manually.',
          );
          this.exchangeRateToUSD.setValue(null, { emitEvent: false });
        }
      },
      error: () => {
        this.exchangeRateToUSD.setValue(null, { emitEvent: false });
        this.isLoading--;
      },
    });
  }

  //#region Save Purchase Order Methods

  /**
   * Main save method - delegates to insert or upsert based on whether PO exists
   */

  savePurchaseOrder() {
    this.formGroup.markAllAsTouched();

    if (this.formGroup.invalid || this.items.invalid) {
      GlobalMessageUtil.warn(
        this.translateService.instant('Warning'),
        this.translateService.instant('Purchase_Order_Fill_Required_Fields'),
      );
      return;
    }

    // Bill To and Ship To are read-only (#5969); when either is empty the site has no company (or address) set up,
    // and the user cannot fix it here.
    if (
      _.isNil(this.billToProvider.value) ||
      _.isNil(this.billToAddress.value) ||
      _.isNil(this.shipToProvider.value) ||
      _.isNil(this.shipToAddress.value)
    ) {
      GlobalMessageUtil.warn(
        this.translateService.instant('Warning'),
        this.translateService.instant('Purchase_Order_Bill_To_Not_Configured'),
      );
      return;
    }

    // Validate destination location for standard PO
    if (this.followsStandardFlow) {
      const hasDestinationFromRequisitions = this.destinationLocationsFromRequisitions.length > 0;
      const hasExistingDestination = this.purchaseOrder?.destinationLocationCodes && this.purchaseOrder.destinationLocationCodes.length > 0;
      const hasManualSelection = !!this.destinationLocationCode.value;

      if (!hasDestinationFromRequisitions && !hasExistingDestination && !hasManualSelection) {
        this.destinationLocationCode.markAsTouched();
        GlobalMessageUtil.warn(
          this.translateService.instant('Warning'),
          this.translateService.instant('Purchase_Order_Select_Destination_Warehouse'),
        );
        return;
      }
    }

    const formValues = this.formGroup.getRawValue();

    const type = this.type.value;

    const purchaseOrder = this.purchaseOrder;

    if (
      _.isNil(type) ||
      _.isNil(formValues.provider) ||
      _.isNil(formValues.paymentMethod) ||
      _.isNil(formValues.shippingMethod) ||
      _.isNil(formValues.paymentTerms) ||
      _.isNil(formValues.shipToProvider) ||
      _.isNil(formValues.shipToAddress) ||
      _.isNil(formValues.shippingTerms) ||
      _.isNil(formValues.billToProvider) ||
      _.isNil(formValues.providerAddress) ||
      _.isNil(formValues.billToAddress)
    ) {
      console.warn('Some required form values are missing:', formValues);
      return;
    }

    const purchaseOrderBase: PurchaseOrderInsert = {
      status: formValues.status,
      type: this.isEditing ? purchaseOrder!.type : type,

      providerReference: formValues.provider.id,
      providerAddressCode: formValues.providerAddress.code.toString(),

      manufacturersReferences: this.buildManufacturersReferences(),
      items: this.buildItemsFromForm(),
      notes: this.notes,
      relatedEntities: this.buildRelatedEntities(),
      files: this.purchaseOrder?.files ?? [],
      issuedDate: this.issuedDate.toISOString(),
      quotedDate: this.quotedDate.toISOString(),

      currency: this.currency.value,
      exchangeRateToUSD: this.currency.value !== 0 ? this.exchangeRateToUSD.value! : 1,
      paymentMethodCode: formValues.paymentMethod,
      paymentTermCode: formValues.paymentTerms,

      billToReference: formValues.billToProvider.id,
      billToAddressCode: formValues.billToAddress.code.toString(),
      shippingMethodCode: formValues.shippingMethod,
      shippingTerm: formValues.shippingTerms,
      shipToReference: formValues.shipToProvider.id,
      shipToAddressCode: formValues.shipToAddress.code.toString(),
      // Include manually selected destination for Standard PO without requisitions
      destinationLocationCodes: this.buildDestinationLocationCodes(),
    };

    const method = this.isEditing
      ? this.purchaseOrderProxyService.updatePurchaseOrder(this.getPurchaseOrderFromInsert(purchaseOrderBase))
      : this.purchaseOrderProxyService.createPurchaseOrder(purchaseOrderBase);

    this.isLoading++;
    method.subscribe({
      next: (resp) => {
        if (_.isNil(resp.data) && !this.isEditing) {
          this.isLoading--;
          return;
        }

        const po = this.isEditing ? this.purchaseOrder! : resp.data!;

        this.items.clear();
        this.isLoading--;
        const action = this.isEditing ? this.translateService.instant('Updated') : this.translateService.instant('Created');
        if (this.isEditing) {
          this.router.navigate(['/purchase-orders']);
        } else {
          this.router.navigate(['/purchase-order-details'], {
            queryParams: { id: po.id },
          });
        }

        GlobalMessageUtil.success(
          this.translateService.instant('Success'),
          this.translateService.instant('Purchase_Order_Save_Success', { number: po.purchaseOrderNumber, action }),
        );
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  /**
   * Check if we're in insert mode (creating new PO)
   */
  private getPurchaseOrderFromInsert(purchaseOrderInsert: PurchaseOrderInsert): PurchaseOrder {
    return {
      ...purchaseOrderInsert,
      id: this.purchaseOrder!.id,
      purchaseOrderNumber: this.purchaseOrder!.purchaseOrderNumber,
    };
  }

  /**
   * Validate exchange rate for insert operations
   */
  private validateExchangeRateForInsert(): boolean {
    if (this.currency.value !== 0 && !this.exchangeRateToUSD.value) {
      GlobalMessageUtil.warn(
        'Purchase Order: Warning',
        'Please provide an exchange rate to USD when a currency other than USD is selected.',
      );
      return false;
    }
    return true;
  }

  /**
   * Helper: Build manufacturers references based on form state
   */
  private buildManufacturersReferences(): string[] {
    if (this.followsStandardFlow) {
      return [];
    }
    return this.manufacturerSameAsProvider.value ? [this.provider.value!.id!] : this.manufacturers.map((m) => m.id!);
  }

  /**
   * Helper: Build items array from form controls
   * Prices are saved in the selected currency (no conversion)
   */
  private buildItemsFromForm(): PurchaseOrderItem[] {
    return this.items.controls.map((item) => {
      // Map isTaxExempt to taxRate: true -> 0, false -> 0.15
      const taxRate = item.controls.isTaxExempt.value ? 0 : 0.15;

      return {
        id: item.controls.id.value,
        itemCode: item.controls.itemCode.value,
        quantity: item.controls.quantity.value,
        uom: Number(item.controls.uom.value),
        // Save prices as-is in the selected currency (no conversion to USD)
        unitPrice: item.controls.unitPrice.value,
        taxRate: taxRate, // Map from isTaxExempt
        totalPrice: item.controls.totalPrice.value,
        discount: item.controls.discount?.value ?? undefined,
        owner: item.controls.owner?.value ?? undefined,
      };
    });
  }

  /**
   * Helper: Build related entities array
   */
  private buildRelatedEntities(): EntityKeyByReference[] {
    const entities: EntityKeyByReference[] = [];

    // Work Orders (Yarn PO)
    const workOrderEntities = this.associateWorkOrders.map((wo) => ({
      entityDefinitionCode: GlobalEntityDefinition.WorkOrder,
      reference: wo.id,
    }));

    // Quotation (Standard PO) - Solo UNA permitida
    if (this.selectedQuotation) {
      entities.push({
        entityDefinitionCode: GlobalEntityDefinition.Quotation,
        reference: this.selectedQuotation.id,
      });
    }

    // External Requisitions (Standard PO) - Múltiples permitidas
    const requisitionEntities = this.associatedRequisitions.map((req) => ({
      entityDefinitionCode: GlobalEntityDefinition.ExternalRequisition,
      reference: req.id!,
    }));

    // Client Purchase Orders (Chemical PO)
    const clientPOEntities = this.associatedClientPOs.map((cpo) => ({
      entityDefinitionCode: CLIENT_PO_ENTITY_DEFINITION_CODE,
      reference: cpo.id,
    }));

    return [...workOrderEntities, ...entities, ...requisitionEntities, ...clientPOEntities];
  }

  /**
   * Helper: Build destination location codes for the PO
   * - Returns codes from associated requisitions if they exist
   * - Returns manually selected code if no requisitions
   * - Returns undefined if neither exists
   */
  private buildDestinationLocationCodes(): string[] | undefined {
    // If we have requisitions with destination codes, use those
    const fromRequisitions = this.destinationLocationsFromRequisitions;
    if (fromRequisitions.length > 0) {
      return fromRequisitions.map((loc) => loc.code);
    }

    // If manually selected (Standard PO without requisitions)
    if (this.followsStandardFlow && this.destinationLocationCode.value) {
      return [this.destinationLocationCode.value];
    }

    // Preserve existing value from loaded PO when editing
    if (this.purchaseOrder?.destinationLocationCodes && this.purchaseOrder.destinationLocationCodes.length > 0) {
      return this.purchaseOrder.destinationLocationCodes;
    }

    return undefined;
  }
  //#endregion

  //#region Shipment Methods
  openShipmentDetailModal(shipment: Shipment): void {
    this.modalService.open(
      ShipmentDetailModalComponent,
      {},
      {
        shipment: shipment,
        purchaseOrderReference: this.purchaseOrder?.id ?? null,
      },
    );
  }

  //#region Yarn Purchase Order Specific Methods - Work Orders and Yarn Management

  /**
   * Search for work orders (Yarn PO only) - using paginated for better performance
   */
  async searchWorkOrders(event: AutoCompleteCompleteEvent): Promise<void> {
    const text = event.query.trim();
    try {
      const { data } = await lastValueFrom(
        this.workOrderProxyService.searchWorkOrder({ orderNumber: text }, 1, 25).pipe(debounceTime(500)),
      );
      this.suggestionWorkOrders = data?.items
        ? data.items.filter((wo) => wo.orderType !== WorkOrderType.BGrade && wo.status !== WorkOrderStatus.Voided)
        : [];
    } catch {
      this.suggestionWorkOrders = [];
    }
  }

  /**
   * Add work order to purchase order (Yarn PO only)
   */
  private addWorkOrder(workOrder: WorkOrder) {
    if (this.associateWorkOrders.find((wo) => wo.id === workOrder.id)) {
      this.generalMessageService('This work order is already associated with this purchase order', 'error');
      return;
    }
    if (workOrder.orderType === WorkOrderType.BGrade) {
      this.generalMessageService('BGrade work orders cannot be added to Yarn purchase orders', 'error');
      return;
    }
    this.associateWorkOrders.push(workOrder);

    workOrder.fabrics.forEach((fabric) => {
      const command: MatchingItemMasterCommand[] = [];
      fabric.fabric?.greige?.yarnDetails.forEach((yd) => {
        const certifications =
          fabric.yarnCertificationDetails?.find((cert) => cert.yarnCode === yd.yarnCode)?.certificationReferences ?? [];
        const yarnCode = yd.yarnCode;
        command.push({
          yarnCode: yarnCode,
          certificationReference: certifications,
        });
      });
      this.getItemMasters(command, workOrder.id, fabric.fabricCode);
    });

    this.transformAssociateWorkOrders();
  }

  /**
   * Fetch item masters for yarn codes (Yarn PO only)
   */
  public getItemMasters(command: MatchingItemMasterCommand[], workOrderReference: string, fabricCode: string): void {
    const yarnCodes = command.map((c) => c.yarnCode).filter((code) => code !== undefined && code !== null);
    this.yarnSearchService.getItemMastersByYarnCode(command).subscribe({
      next: (response) => {
        if (!_.isNil(response.data) && !_.isEmpty(response.data)) {
          yarnCodes.forEach((yc) => {
            const yarnUniqueKey = this.buildUniqueKey(yc, workOrderReference, fabricCode);
            this.yarnUniqueKeyAndItemMaster[yarnUniqueKey] = response.data![yc];
          });
        } else {
          yarnCodes.forEach((yc) => {
            const yarnUniqueKey = this.buildUniqueKey(yc, workOrderReference, fabricCode);
            this.yarnUniqueKeyAndItemMaster[yarnUniqueKey] = null; // Ensure it exists
          });
        }
      },
      error: () => {
        yarnCodes.forEach((yc) => {
          const yarnUniqueKey = this.buildUniqueKey(yc, workOrderReference, fabricCode);
          this.yarnUniqueKeyAndItemMaster[yarnUniqueKey] = null; // Ensure it exists even if error occurs
          console.error(`Error fetching item master for yarn code: ${yc}`);
        });
      },
    });
  }

  /**
   * Get certifications for a specific yarn (Yarn PO only)
   */
  getYarnCertifications(yarnCode: string, workOrder: WorkOrder, fabricCode: string): Certification[] {
    const references =
      workOrder.fabrics.find((fabric) => fabric.fabricCode === fabricCode)?.yarnCertificationDetails?.find((yc) => yc.yarnCode === yarnCode)
        ?.certificationReferences ?? [];

    return this.certifications.filter((cert) => references.includes(cert.id));
  }

  /**
   * Handle work order selection (Yarn PO only)
   */
  onSelectWorkOrder($event: AutoCompleteSelectEvent) {
    this.addWorkOrder($event.value);
  }

  /**
   * Remove work order from purchase order (Yarn PO only)
   */
  public removeWorkOrder(workOrder: WorkOrder) {
    this.associateWorkOrders = this.associateWorkOrders.filter((wo) => wo.id !== workOrder.id);
    this.transformAssociateWorkOrders();
  }

  /**
   * Search items by selected work order (Yarn PO only)
   */
  async searchParamsByWorkOrder(event: AutoCompleteCompleteEvent): Promise<void> {
    const text = event.query.trim();
    this.filteredEntities = [];
    const items: ItemMaster[] = [];
    //get yarns
    this.WorkOrderSelectedToSearch.value?.fabrics.forEach((fabric) => {
      fabric.fabric?.greige?.yarnDetails.forEach((yarn) => {
        const yarnUniqueKey = this.buildUniqueKey(yarn.yarnCode!, this.WorkOrderSelectedToSearch.value!.id, fabric.fabricCode);
        if (this.yarnUniqueKeyAndItemMaster[yarnUniqueKey]) {
          const itemMaster = this.yarnUniqueKeyAndItemMaster[yarnUniqueKey]!;
          itemMaster.fieldToDisplay = `(${itemMaster.itemCode})${this.localeHelperService.getLocalizedValue(itemMaster.itemLongDisplayName)}`;
          items.push(itemMaster);
        }
      });
    });

    this.filteredEntities = items.filter(
      (item) =>
        this.localeHelperService.getLocalizedValue(item?.itemDescription).toLowerCase().includes(text.toLowerCase()) ||
        item.itemCode.toLowerCase().includes(text.toLowerCase()),
    );
  }

  getItemsOfEntities = async (dataToSearch: string[]): Promise<ItemMaster[]> => {
    return await lastValueFrom(
      this.itemSearchService.getItemCodesBatch({ itemCodes: dataToSearch! }).pipe(
        map((response: ApiResponse<ItemMaster[]>) => {
          const items = response.data ?? [];
          const existingItemCodes = this.items.controls.map((control) => control.get('itemCode')?.value);

          return items
            .map((item) => ({
              ...item,
              fieldToDisplay: item.itemCode + ' ' + this.localeHelperService.getLocalizedValue(item.itemLongDisplayName),
            }))
            .filter((item) => !existingItemCodes.includes(item.itemCode));
        }),
        catchError(() => of([])),
      ),
    );
  };

  /**
   * Transform work orders into fabric and yarn details (Yarn PO only)
   */
  transformAssociateWorkOrders() {
    if (!this.associateWorkOrders) return [];

    const fabrics = (this.associateWorkOrders || [])
      .flatMap((wo) =>
        wo?.fabrics.map((fabric) => ({
          ...fabric,
          uom: wo.uom,
        })),
      )
      .flatMap((fabric) => {
        if (!fabric || !fabric.fabric) return [];
        return {
          fabricCode: fabric.fabricCode || '',
          longDescription: fabric.fabric?.longDescription || '',
          totalQuantity: fabric.quantityInUOM || 0,
          itemUOM: fabric.uom,
          yarns: (fabric.fabric?.greige?.yarnDetails || []).map((yarn) => ({
            yarnCode: yarn.yarnCode || '',
            yarnDescription: yarn.yarn?.longDescription || '',
            quantity: ((yarn.usePercentage ?? 0) / 100) * (fabric.quantityInUOM || 0),
            percentage: yarn.usePercentage || 0,
          })),
        };
      });

    const accumulatedFabrics = fabrics.reduce(
      (acc, fabric) => {
        const existingFabric = acc.find((f) => f.fabricCode === fabric.fabricCode);

        if (existingFabric) {
          existingFabric.totalQuantity += fabric.totalQuantity;
          existingFabric.yarns.forEach((yarn, index) => {
            if (fabric.yarns[index]) {
              yarn.quantity += fabric.yarns[index].quantity;
            }
          });
        } else {
          acc.push({ ...fabric });
        }

        return acc;
      },
      [] as typeof fabrics,
    );

    return accumulatedFabrics.length > 0 ? accumulatedFabrics : [];
  }

  searchRelatedEntities(relatedEntities: { reference: string; entityDefinitionCode: number }[]): void {
    relatedEntities.forEach((entity) => {
      switch (entity.entityDefinitionCode) {
        case GlobalEntityDefinition.WorkOrder:
          this.workOrderProxyService.getWorkOrderByIdNotArray(entity.reference).subscribe({
            next: ({ data }) => {
              if (data) {
                if (this.associateWorkOrders.find((wo) => wo.id === data.id)) {
                  return;
                }
                if (this.isYarnPurchaseOrder && data.orderType === WorkOrderType.BGrade) {
                  return;
                }
                this.associateWorkOrders.push(data);
                const distinctYarnCodes = new Set<string>();
                data.fabrics.forEach((fabric) => {
                  fabric.fabric?.greige?.yarnDetails.forEach((yarn) => {
                    distinctYarnCodes.add(yarn.yarnCode!);
                  });
                  data.fabrics.forEach((fabric) => {
                    const command: MatchingItemMasterCommand[] = [];
                    fabric.fabric?.greige?.yarnDetails.forEach((yd) => {
                      const certifications =
                        fabric.yarnCertificationDetails?.find((cert) => cert.yarnCode === yd.yarnCode)?.certificationReferences ?? [];
                      const yarnCode = yd.yarnCode;
                      command.push({
                        yarnCode: yarnCode,
                        certificationReference: certifications,
                      });
                    });
                    this.getItemMasters(command, data.id, fabric.fabricCode);
                  });
                });
              }
            },
          });
          break;

        case GlobalEntityDefinition.Quotation:
          // Cargar la Quotation
          this.quotationProxyService.getById(entity.reference).subscribe({
            next: (response) => {
              if (response.data) {
                this.selectedQuotation = response.data;
                this.formControlQuotation.setValue(response.data, { emitEvent: false });
                this.quotation = response.data;
                this.formControlQuotation.disable();

                // Cargar provider y bloquearlo
                if (response.data.providerReference) {
                  this.loadProviderFromQuotation(response.data.providerReference);
                }

                // NO poblar items aquí porque ya fueron cargados desde la PO guardada
                // Los items ya tienen las cantidades y owners correctos desde fetchItems()
              }
            },
            error: (error) => {
              console.error('Error loading quotation:', error);
            },
          });
          break;

        case GlobalEntityDefinition.ExternalRequisition:
          // Cargar cada External Requisition
          this.requisitionProxyService.getRequisitionById(entity.reference).subscribe({
            next: (response) => {
              if (response.data && !this.associatedRequisitions.find((r) => r.id === response.data!.id)) {
                this.associatedRequisitions.push(response.data);

                // Establecer la última requisition como currentRequisitionOwner
                this.currentRequisitionOwner = {
                  entityDefinitionCode: GlobalEntityDefinition.ExternalRequisition,
                  reference: response.data.id!,
                  number: response.data.number,
                };
              }
            },
            error: (error) => {
              console.error('Error loading requisition:', error);
            },
          });
          break;

        case CLIENT_PO_ENTITY_DEFINITION_CODE:
          this.clientPurchaseOrderProxyService.getById(entity.reference).subscribe({
            next: (response) => {
              if (response.data && !this.associatedClientPOs.find((c) => c.id === response.data!.id)) {
                this.associatedClientPOs.push(response.data);
              }
            },
            error: (error) => {
              console.error('Error loading client purchase order:', error);
            },
          });
          break;
      }
    });
  }

  //#region Chemical Purchase Order Specific Methods - Client Purchase Orders

  async searchClientPurchaseOrders(event: AutoCompleteCompleteEvent): Promise<void> {
    const text = event.query.trim();
    if (!text) {
      this.suggestedClientPOs = [];
      return;
    }
    const command: ClientPurchaseOrderSearchCommand = { number: text };
    this.clientPurchaseOrderProxyService.search(command, 1, 25).subscribe({
      next: (response) => {
        this.suggestedClientPOs = (response.data?.items || []).map((cpo) => ({
          ...cpo,
          fieldToDisplay: cpo.number,
        }));
      },
      error: () => {
        this.suggestedClientPOs = [];
      },
    });
  }

  onSelectClientPO(event: AutoCompleteSelectEvent): void {
    const cpo = event.value as ClientPurchaseOrder;
    if (this.associatedClientPOs.find((c) => c.id === cpo.id)) {
      GlobalMessageUtil.warn(this.translateService.instant('Warning'), 'This Client Purchase Order is already associated.');
      this.formControlClientPO.setValue(null);
      return;
    }
    this.associatedClientPOs.push(cpo);
    this.formControlClientPO.setValue(null);

    this.clientPurchaseOrderProxyService.getDetailsById(cpo.id).subscribe({
      next: (response) => {
        if (!response.data) return;
        const cpoOwner: EntityKey = {
          entityDefinitionCode: CLIENT_PO_ENTITY_DEFINITION_CODE,
          reference: cpo.id,
          number: cpo.number,
        };
        response.data.items.forEach((line) => {
          if (line.item) {
            this.items.push(this.createItemFormGroup(line.item, undefined, line.quantity, line.unitPrice, cpoOwner, false, line.uom));
          }
        });
      },
    });
  }

  removeClientPO(index: number): void {
    this.associatedClientPOs.splice(index, 1);
  }

  //#endregion

  //#region Standard Purchase Order Specific Methods - Quotations and Requisitions Management

  /**
   * Busca quotations basado en número o referencia del proveedor
   */
  async searchQuotations(event: AutoCompleteCompleteEvent): Promise<void> {
    const text = event.query.trim();
    if (!text) {
      this.suggestedQuotations = [];
      return;
    }

    const searchCommand: QuotationSearchCommand = {
      number: text,
    };

    this.quotationProxyService.search(searchCommand).subscribe({
      next: (response) => {
        this.suggestedQuotations = (response.data || []).map((q) => ({
          ...q,
          fieldToDisplay: `${q.number} - ${q.providerName || 'N/A'}`,
        }));
      },
      error: () => {
        this.suggestedQuotations = [];
      },
    });
  }

  /**
   * Maneja la selección de una Quotation
   */
  onSelectQuotation(event: AutoCompleteSelectEvent): void {
    const quotation: Quotation = event.value;

    // Validación: Solo UNA quotation
    if (this.selectedQuotation) {
      GlobalMessageUtil.error(this.translateService.instant('Error'), this.translateService.instant('Purchase_Order_Only_One_Quotation'));
      this.formControlQuotation.reset();
      return;
    }

    // Validar que la quotation tenga provider
    if (!quotation.providerReference) {
      GlobalMessageUtil.error(
        this.translateService.instant('Error'),
        this.translateService.instant('Purchase_Order_Quotation_No_Provider'),
      );
      this.formControlQuotation.reset();
      return;
    }

    // Validar que la quotation tenga items
    if (!quotation.items || quotation.items.length === 0) {
      GlobalMessageUtil.error(this.translateService.instant('Error'), this.translateService.instant('Purchase_Order_Quotation_No_Items'));
      this.formControlQuotation.reset();
      return;
    }

    // Advertir si la quotation está expirada
    if (quotation.expirationDate && new Date(quotation.expirationDate) < new Date()) {
      GlobalMessageUtil.warn(this.translateService.instant('Warning'), this.translateService.instant('Purchase_Order_Quotation_Expired'));
    }

    // Guardar quotation
    this.selectedQuotation = quotation;

    // Cargar y bloquear provider
    this.loadProviderFromQuotation(quotation.providerReference);

    // Auto-poblar campos financieros
    this.currency.setValue(quotation.currency, { emitEvent: false });
    this.exchangeRateToUSD.setValue(quotation.exchangeRateToUSD, { emitEvent: false });
    if (quotation.paymentMethodCode) {
      this.paymentMethod.setValue(quotation.paymentMethodCode, { emitEvent: false });
    }

    // Cargar la requisition original primero, y luego poblar items con las cantidades de la requisition
    this.loadInitialRequisition(quotation.initiator.reference, (requisition) => {
      // Auto-poblar items con las cantidades de la requisition
      this.populateItemsFromQuotation(quotation, requisition);
    });

    // Deshabilitar control de quotation
    this.formControlQuotation.disable();

    GlobalMessageUtil.success(
      this.translateService.instant('Success'),
      this.translateService.instant('Purchase_Order_Quotation_Loaded', { number: quotation.number }),
    );
  }

  /**
   * Carga el provider desde la quotation y lo bloquea
   */
  private loadProviderFromQuotation(providerReference: string): void {
    this.isLoading++;
    this.businessProxyService.getById(providerReference).subscribe({
      next: (response) => {
        if (response.data) {
          const provider = {
            ...response.data,
            fieldToDisplay: `${response.data.code} - ${response.data.name}`,
          };
          this.providers.push(provider);
          this.provider.setValue(provider, { emitEvent: false });
          this.provider.disable(); // BLOQUEAR provider
        }
        this.isLoading--;
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  /**
   * Popula los items del PO desde la quotation y requisition
   */
  private populateItemsFromQuotation(quotation: Quotation, requisition?: ExternalRequisition): void {
    const itemCodes = quotation.items.map((qi) => qi.itemCode);

    this.itemSearchService.getItemCodesBatch({ itemCodes }).subscribe({
      next: (response) => {
        const items = response.data || [];

        this.items.clear();

        items.forEach((item) => {
          const quotationItem = quotation.items.find((qi) => qi.itemCode === item.itemCode);
          if (quotationItem) {
            // Buscar la cantidad en la requisition si está disponible
            let quantity: number | undefined = undefined;
            if (requisition && requisition.entries) {
              const reqItem = requisition.entries.find((entry) => entry.itemCode === item.itemCode);
              if (reqItem) {
                quantity = reqItem.quantity;
              }
            }

            // Usar el currentRequisitionOwner si está disponible
            this.items.push(this.createItemFormGroup(item, undefined, quantity, quotationItem.unitPrice, this.currentRequisitionOwner));
          }
        });
      },
    });
  }

  /**
   * Carga la requisition inicial que originó la quotation
   */
  private loadInitialRequisition(requisitionReference: string, callback?: (requisition: ExternalRequisition) => void): void {
    this.requisitionProxyService.getRequisitionById(requisitionReference).subscribe({
      next: (response) => {
        if (response.data) {
          this.associatedRequisitions.push(response.data);

          // Establecer como currentRequisitionOwner
          this.currentRequisitionOwner = {
            entityDefinitionCode: GlobalEntityDefinition.ExternalRequisition,
            reference: response.data.id!,
            number: response.data.number,
          };

          // Ejecutar callback si existe
          if (callback) {
            callback(response.data);
          }
        }
      },
    });
  }

  /**
   * Busca requisitions aprobadas compatibles con la quotation seleccionada
   */
  async searchApprovedRequisitions(event: AutoCompleteCompleteEvent): Promise<void> {
    if (!this.selectedQuotation) {
      GlobalMessageUtil.warn(
        this.translateService.instant('Warning'),
        this.translateService.instant('Purchase_Order_Select_Quotation_First'),
      );
      return;
    }

    const text = event.query.trim();
    if (!text) {
      this.suggestedRequisitions = [];
      return;
    }

    const quotationItemCodes = this.selectedQuotation.items.map((i) => i.itemCode);

    // Buscar requisitions con filtro básico - usando paginación para mejor rendimiento
    this.requisitionProxyService
      .searchRequisition(
        {
          number: text,
        },
        false,
        true,
        1,
        50,
      )
      .subscribe({
        next: (response) => {
          const allRequisitions = response.data?.items || [];

          // Filtrar en el frontend
          this.suggestedRequisitions = allRequisitions
            .filter((req) => {
              // Filtro 1: Debe ser una requisition hija (tener parentRequisitionReference)
              const isChildRequisition = !!req.parentRequisitionReference;

              // Filtro 2: Debe estar aprobada (status name = 'Approved')
              const isApproved = req.statusName?.en === 'Approved' || req.statusName?.es === 'Aprobada';

              // Filtro 3: Debe tener al menos un item de la quotation
              const hasCompatibleItems = req.entries?.some((entry) => quotationItemCodes.includes(entry.itemCode));

              // Filtro 4: No debe estar ya en la lista
              const alreadyAdded = this.associatedRequisitions.some((r) => r.id === req.id);

              return isChildRequisition && isApproved && hasCompatibleItems && !alreadyAdded;
            })
            .map((req) => ({
              ...req,
              fieldToDisplay: `${req.number} - ${req.departmentName || 'N/A'}`,
            }));
        },
        error: () => {
          this.suggestedRequisitions = [];
        },
      });
  }

  /**
   * Maneja la selección de una External Requisition adicional
   */
  onSelectRequisition(event: AutoCompleteSelectEvent): void {
    const requisition: ExternalRequisition = event.value;

    if (this.associatedRequisitions.find((r) => r.id === requisition.id)) {
      GlobalMessageUtil.error(
        this.translateService.instant('Error'),
        this.translateService.instant('Purchase_Order_Requisition_Already_Added'),
      );
      this.formControlRequisition.reset();
      return;
    }

    if (requisition.statusName?.en !== 'Approved' && requisition.statusName?.es !== 'Aprobada') {
      GlobalMessageUtil.error(
        this.translateService.instant('Error'),
        this.translateService.instant('Purchase_Order_Only_Approved_Requisitions'),
      );
      this.formControlRequisition.reset();
      return;
    }

    // Validación 3: Debe tener items compatibles
    const quotationItemCodes = this.selectedQuotation!.items.map((i) => i.itemCode);
    const hasCompatibleItems = requisition.entries?.some((entry) => quotationItemCodes.includes(entry.itemCode));

    if (!hasCompatibleItems) {
      GlobalMessageUtil.error(
        this.translateService.instant('Error'),
        this.translateService.instant('Purchase_Order_Requisition_No_Compatible_Items'),
      );
      this.formControlRequisition.reset();
      return;
    }

    // Agregar a la lista
    this.associatedRequisitions.push(requisition);

    // Establecer como currentRequisitionOwner para que los nuevos items agregados usen esta requisition
    const requisitionOwner: EntityKey = {
      entityDefinitionCode: GlobalEntityDefinition.ExternalRequisition,
      reference: requisition.id!,
      number: requisition.number,
    };
    this.currentRequisitionOwner = requisitionOwner;

    // Agregar los items de la requisition que están en la quotation
    this.addItemsFromRequisition(requisition, requisitionOwner);

    this.formControlRequisition.reset();

    GlobalMessageUtil.success(
      this.translateService.instant('Success'),
      this.translateService.instant('Purchase_Order_Requisition_Added', { number: requisition.number }),
    );
  }

  private addItemsFromRequisition(requisition: ExternalRequisition, owner: EntityKey): void {
    if (!requisition.entries || !this.selectedQuotation) {
      return;
    }

    const quotationItemCodes = this.selectedQuotation.items.map((i) => i.itemCode);

    // Filtrar solo los items de la requisition que están en la quotation
    const itemCodesToAdd = requisition.entries
      .filter((entry) => quotationItemCodes.includes(entry.itemCode))
      .map((entry) => entry.itemCode);

    if (itemCodesToAdd.length === 0) {
      return;
    }

    // Cargar los ItemMaster para estos items
    this.itemSearchService.getItemCodesBatch({ itemCodes: itemCodesToAdd }).subscribe({
      next: (response) => {
        const items = (response.data || []).filter((item) => this.isItemAllowedForOrderType(item));

        items.forEach((item) => {
          // Obtener datos de la quotation y requisition
          const quotationItem = this.selectedQuotation!.items.find((qi) => qi.itemCode === item.itemCode);
          const reqEntry = requisition.entries!.find((entry) => entry.itemCode === item.itemCode);

          // Siempre agregar como nueva fila con su propio owner
          // No importa si ya existe el mismo itemCode, cada requisition tiene su propia fila
          const quantity = reqEntry?.quantity;
          const unitPrice = quotationItem?.unitPrice;

          this.items.push(this.createItemFormGroup(item, undefined, quantity, unitPrice, owner, true));
        });

        GlobalMessageUtil.success(
          this.translateService.instant('Success'),
          this.translateService.instant('Purchase_Order_Items_Added_From_Requisition', {
            count: items.length,
            number: requisition.number,
          }),
        );
      },
    });
  }

  removeRequisition(requisition: ExternalRequisition): void {
    // Desvincular items que pertenecen a esta requisition (owner = null) para poder reasignarlos
    const requisitionReference = requisition.id!;
    for (let i = this.items.length - 1; i >= 0; i--) {
      const itemOwner = this.items.at(i).controls.owner?.value;
      if (itemOwner && itemOwner.reference === requisitionReference) {
        this.items.at(i).controls.owner.setValue(null);
      }
    }

    this.associatedRequisitions = this.associatedRequisitions.filter((r) => r.id !== requisition.id);

    // Si se removió la requisition que era el currentRequisitionOwner, actualizar al último disponible
    if (this.currentRequisitionOwner?.reference === requisitionReference) {
      if (this.associatedRequisitions.length > 0) {
        const lastRequisition = this.associatedRequisitions[this.associatedRequisitions.length - 1];
        this.currentRequisitionOwner = {
          entityDefinitionCode: GlobalEntityDefinition.ExternalRequisition,
          reference: lastRequisition.id!,
          number: lastRequisition.number,
        };
      } else {
        this.currentRequisitionOwner = null;
      }
    }

    GlobalMessageUtil.success(
      this.translateService.instant('Success'),
      this.translateService.instant('Purchase_Order_Requisition_Removed', { number: requisition.number }),
    );
  }

  /**
   * Obtiene los items de una requisition que están en la quotation
   */
  getRequisitionItemsInQuotation(requisition: ExternalRequisition): ExternalRequestEntry[] {
    if (!this.selectedQuotation || !requisition.entries) {
      return [];
    }

    const quotationItemCodes = this.selectedQuotation.items.map((i) => i.itemCode);

    return requisition.entries.filter((entry) => quotationItemCodes.includes(entry.itemCode));
  }

  /**
   * Obtiene el precio de un item desde la quotation
   */
  getQuotationPrice(itemCode: string): number {
    const quotationItem = this.selectedQuotation?.items.find((i) => i.itemCode === itemCode);
    return quotationItem?.unitPrice || 0;
  }

  /**
   * Obtiene la descripción de un item
   */
  getItemDescription(itemCode: string): string {
    const item = this.items.controls.find((ctrl) => ctrl.controls.itemCode.value === itemCode);
    return item?.controls.longDescription?.value || 'N/A';
  }

  //#endregion

  /**
   * Get item master code from yarn unique key (Yarn PO only)
   */
  getYarnCodeItemMasterCode(uniqueKey: string): string {
    const itemMaster = this.yarnUniqueKeyAndItemMaster[uniqueKey];
    if (itemMaster) {
      return itemMaster.itemCode;
    }
    return 'Not Found';
  }

  /**
   * Groups yarn details by yarn code and sums up the use percentages (Yarn PO only)
   * This handles cases where the same yarn code appears multiple times with different loop lengths
   */
  getAggregatedYarnDetails(
    yarnDetails: YarnDetail[],
    workOrderReference: string,
    fabricCode: string,
  ): { yarnCode: string; yarn: unknown; usePercentage: number; uniqueKey: string }[] {
    if (!yarnDetails || yarnDetails.length === 0) {
      return [];
    }

    // Group by yarn code
    const groupedByYarnCode = yarnDetails.reduce(
      (acc, yarnDetail) => {
        const yarnCode = yarnDetail.yarnCode;

        if (!acc[yarnCode]) {
          acc[yarnCode] = {
            yarnCode: yarnCode,
            yarn: yarnDetail.yarn,
            usePercentage: 0,
            uniqueKey: this.buildUniqueKey(yarnCode, workOrderReference, fabricCode),
          };
        }

        // Sum up the use percentages
        acc[yarnCode].usePercentage += yarnDetail.usePercentage || 0;

        return acc;
      },
      {} as Record<string, { yarnCode: string; yarn: unknown; usePercentage: number; uniqueKey: string }>,
    );

    // Convert to array and round percentages to 2 decimal places
    const aggregatedArray = Object.values(groupedByYarnCode);
    return aggregatedArray.map((item) => ({
      yarnCode: item.yarnCode,
      yarn: item.yarn,
      usePercentage: parseFloat(item.usePercentage.toFixed(2)),
      uniqueKey: item.uniqueKey,
    }));
  }
  //#endregion

  generalMessageService(message: string, type: string) {
    switch (type) {
      case 'success':
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: message,
          life: 2500,
          key: 'main',
        });
        break;
      case 'error':
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: message,
          life: 2500,
          key: 'main',
        });
        break;
    }
  }

  getExtensionFileName(nameFile: string): string {
    return nameFile.split('.').pop() ?? '';
  }

  downloadFile(url: string) {
    window.open(url, '_blank');
  }

  handleDelete(event: { item: MasterData; confirmCallback: () => void; cancelCallback: () => void }) {
    MasterDataActionUtil.attemptToDeleteMasterData(event.item).subscribe({
      next: (confirmed) => {
        if (confirmed) {
          event.confirmCallback();
        } else {
          event.cancelCallback();
        }
      },
    });
  }

  showMessagesApi(event: ApiResponse<MasterData>) {
    if (event.success) {
      GlobalMessageUtil.success(this.translateService.instant('Success'), this.translateService.instant('Purchase_Order_Records_Deleted'));
    } else if (typeof event.errorMessage !== 'string') {
      GlobalMessageUtil.error(
        this.translateService.instant('Error'),
        getLocalizedValue(this.translateService, event.errorMessage) || this.translateService.instant('Purchase_Order_Request_Error'),
      );
    }
  }

  formatAmountNumber(number: number, fixed = 2) {
    return number.toFixed(fixed).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  }

  goToInternalLink(): void {
    window.open('https://scribehow.com/shared/Creating_a_Purchase_Order_in_Sierra_ERP__IfK87TlhQtaLioKi-nBB0Q');
  }

  copyPurchaseOrder(): void {
    if (!this.purchaseOrder?.id) {
      return;
    }

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/purchase-order-details'], {
        queryParams: {
          id: 'new',
          copyFromPoReference: this.purchaseOrder.id,
          orderType: this.purchaseOrder.type,
        },
      }),
    );

    window.open(url, '_blank');
  }

  getKnittingWithLoss(quantity: number, fabric: WorkOrderFabric, orderType: number): number {
    return YarnQuantityUtil.calculateWithLoss(quantity, fabric.fabric, orderType);
  }

  getLocalCurrencyPrice(price: number): number {
    if (this.showHnlEquivalent) {
      return this.getValueOnSelectedCurrency(price);
    }
    return price;
  }

  /**
   * Auto-populate items from work order into purchase order (Yarn PO only)
   */
  autoPopulateWOIntoItems(associateWorkOrder: WorkOrder): void {
    associateWorkOrder?.fabrics.forEach((fabric) => {
      fabric.fabric?.greige?.yarnDetails.forEach(async (yarn) => {
        const yarnUniqueKey = this.buildUniqueKey(yarn.yarnCode!, associateWorkOrder.id, fabric.fabricCode);
        if (this.yarnUniqueKeyAndItemMaster[yarnUniqueKey]) {
          const itemMaster = this.yarnUniqueKeyAndItemMaster[yarnUniqueKey]!;
          const quantity = fabric.quantityInPounds * (yarn.usePercentage! / 100);
          const quantityWithLoss = this.getKnittingWithLoss(quantity, fabric, associateWorkOrder.orderType);
          itemMaster.fieldToDisplay = `(${itemMaster.itemCode})${this.localeHelperService.getLocalizedValue(itemMaster.itemLongDisplayName)}`;
          const alreadyExists = this.items.value.some((ctrl) => ctrl.itemCode === itemMaster.itemCode);
          if (alreadyExists) {
            // add quantity to existing item
            const existingItem = this.items.value.find((ctrl) => ctrl.itemCode === itemMaster.itemCode);
            if (existingItem) {
              const newQuantity = (existingItem.quantity ?? 0) + quantityWithLoss;
              existingItem.quantity = newQuantity;
              existingItem.totalPrice = newQuantity * (existingItem.unitPrice ?? 0);
              this.items.patchValue(this.items.value);
            }
          } else {
            // Para Yarn PO, no hay owner de requisition, se usa null o undefined
            this.items.push(this.createItemFormGroup(itemMaster, undefined, quantityWithLoss, undefined, null));
          }
        }
      });
    });
  }
  onViewMore(): void {
    this.openNotesModal();
  }

  openNotesModal(): void {
    const modalData = {
      notes: this.notes,
      userLite: this.userLite,
      viewOnly: !this.canEdit(),
    };

    this.modalService.open(NotesSidebarComponent, {}, modalData).subscribe({
      next: (result) => {
        if (result && result.action === 'noteAdded') {
          this.notes = result.data.notes;
        }
      },
      error: (error) => {
        console.error('Error handling notes modal result:', error);
      },
    });
    setTimeout(() => {
      const modalElement = document.querySelector('.panel.modal') as HTMLElement;
      if (modalElement) modalElement.style.maxWidth = '50%';
      modalElement.style.width = '50%';
    }, 100);
  }

  // #region Report Generation Methods
  canPrintReport(): boolean {
    if (!this.purchaseOrder?.id) return false;

    // Standard and Administrative POs require Approved status or higher
    if (this.purchaseOrder.type === PurchaseOrderType.Standard || this.purchaseOrder.type === PurchaseOrderType.Administrative) {
      return this.purchaseOrder.status >= PurchaseOrderStatus.Approved;
    }

    // For Yarn POs, allow printing without status restriction
    if (this.purchaseOrder.type === PurchaseOrderType.Yarn) {
      return true;
    }

    return false;
  }

  downloadReport(): void {
    if (!this.canPrintReport()) {
      GlobalMessageUtil.error(
        this.translateService.instant('Error'),
        this.translateService.instant('Purchase_Order_Print_Approved_Required'),
      );
      return;
    }
    this.isLoading++;
    this.purchaseOrderProxyService.downloadReport(this.purchaseOrder!.id).subscribe({
      next: (blob: Blob) => {
        this.isLoading--;
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      },
      error: () => {
        this.isLoading--;
      },
    });
  }
  //#endregion
}
